import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";

import {
  huidigAccessToken,
  huidigRefreshToken,
  useAuthStore,
} from "@/lib/auth-store";
import type {
  AdviesregelAntwoord,
  AiModelAntwoord,
  AiModelWijzigen,
  AllergieInvoer,
  AutomatiseringActie,
  AutomatiseringConfig,
  AutomatiseringOverzichtItem,
  BewakingAntwoord,
  BewakingInstellingen,
  OpschoningAntwoord,
  OpschoningInstellingen,
  CombinatievoorstelAntwoord,
  AvgAnonimiseerBevestiging,
  AvgAnonimiseerUitkomst,
  AvgVoorbereiding,
  AvgExportAntwoord,
  Commitment,
  CommitmentStatus,
  ConversatieDetail,
  EmailAntwoord,
  EmailBijlagenAntwoord,
  EmailBundelAntwoord,
  EmailCategorie,
  EmailDetailAntwoord,
  EmailLijstAntwoord,
  EmailTellersAntwoord,
  EmailStatus,
  EmailTabId,
  EmailThreadAntwoord,
  GebruikerAanmaken,
  GebruikerAntwoord,
  GebruikerWijzigen,
  MailboxAntwoord,
  MedewerkerAanmaken,
  MedewerkerAntwoord,
  MedewerkerPreviewAntwoord,
  MedewerkerVoorToewijzing,
  MedewerkerWijzigen,
  SignatureSettingsAntwoord,
  SignatureSettingsWijzigen,
  DierinschattingAntwoord,
  HuisdierAanmaken,
  HuisdierAntwoord,
  HuisdierWijzigen,
  RekentoolAanvraag,
  RekentoolConfig,
  RekentoolResultaat,
  KlantAntwoord,
  ZoekHerindexAntwoord,
  ZoekInstellingenAntwoord,
  ZoekInstellingenWijzigen,
  ZoekNulAntwoord,
  ZoekOpdrachtenAntwoord,
  ZoekOverzichtAntwoord,
  ZoekSynoniemenAntwoord,
  ZoekSynoniemInvoer,
  KlantDossierAntwoord,
  KlantDossierVolledig,
  KlantLijstAntwoord,
  KlantRelatiesAntwoord,
  KlantRetour,
  KlantReview,
  KlantVeldVerrijking,
  KlantWijzigen,
  KennisItemAanmaken,
  KennisItemAntwoord,
  KanaalCallsAntwoord,
  KanaalDagenAntwoord,
  KanaalItemsAntwoord,
  KostenOverzichtAntwoord,
  KostenPeriode,
  KennisItemSchrijfAntwoord,
  KennisItemWijzigen,
  KennisOpstelhulpVoorstel,
  KennisVoorstelAccepteren,
  KennisVoorstelCheck,
  KennisVoorstelResultaat,
  SyncStatusAntwoord,
  KoppelingVoorstel,
  SparGeschiedenisAntwoord,
  LoginAanvraag,
  LoginStap1Antwoord,
  LoginTweeFAAanvraag,
  OrderAntwoord,
  OrdersOverzichtAntwoord,
  OrderRegelsAntwoord,
  TrackingZoekAntwoord,
  ZendingAntwoord,
  ZoekAntwoord,
  AdresSuggestieAntwoord,
  SchrijfhulpAntwoord,
  SchrijfhulpModus,
  AdminChatBericht,
  AdminChatSessieDetail,
  AdminChatSessieLijst,
  AdminChatWeergave,
  ChatKanaal,
  ChatSchrijfhulpAntwoord,
  ChatTellersAntwoord,
  WhatsAppDoelAntwoord,
  WhatsAppTemplateItem,
  WhatsAppTemplateVerstuurd,
  EmailDashboard,
  HerbestelTrechterAntwoord,
  HerbestelTrechterKlantenAntwoord,
  HoofdDashboard,
  HugoSleutel,
  HugoSleutelAangemaakt,
  TokenAntwoord,
  TweeFASetupAntwoord,
  PushAbonnementAntwoord,
  PushAbonnementInvoer,
  PushConfigAntwoord,
  PushApparaat,
  PushApparatenAntwoord,
  PushOpruimAntwoord,
  PushTestAntwoord,
  ReviewAutopublishAntwoord,
  ReviewAutopublishWijzigen,
  ReviewBulkGoedkeurenAntwoord,
  ReviewConceptLijstAntwoord,
  ReviewResponseAntwoord,
  MailLabelAntwoord,
  MailLabelKort,
  HerbestelAanvraagLogAntwoord,
  HerbestelAanvraagAntwoord,
  HerbestelAfrekenenAntwoord,
  HerbestelAnderAdres,
  HerbestelBetalenAntwoord,
  HerbestelMeetStap,
  HerbestelOfferteAntwoord,
  HerbestelBetalingenAntwoord,
  HerbestelTestlinkAntwoord,
  HerbestelOrderStandAntwoord,
  HerbestelBetalingStatusAntwoord,
  HerbestelPaginaAntwoord,
  HerbestelStandAntwoord,
  ReviewsScoreAntwoord,
  ShopExtrasOverzichtAntwoord,
  ShopExtraWijzigen,
  SjabloonAntwoord,
  StoringenAntwoord,
  RecoveryAfmeldAntwoord,
  RecoveryOverzichtAntwoord,
  CheckoutOptinOverzichtAntwoord,
  CheckoutAnkerOverzicht,
  CheckoutAssetOverzicht,
  CheckoutAssetStand,
  CheckoutLaagstand,
  CheckoutMeetlat,
  CheckoutMeetlatIndeling,
  CheckoutOrderControle,
  CheckoutSignalen,
  CheckoutTrechterAntwoord,
  CheckoutVerloopAntwoord,
  VoorraadmeldingenAntwoord,
  WerkvoorraadAntwoord,
  WerkvoorraadTab,
  WerkvoorraadTellersAntwoord,
  HistorieAntwoord,
  HistorieParams,
  WerkvoorraadWeergave,
  VraagtypeMeter,
} from "@/types/api";
import type { EmailTypfoutConfig } from "@/lib/emailTypfouten";
import {
  isToetsWeigering,
  meldToetsWeigering,
  TOETS_WEIGERING_TEKST,
} from "@/lib/toetsWeigering";

/**
 * API base URL.
 * - In development draait Vite op 5173 en proxiet /api (en /webhooks,
 *   /health) naar localhost:8000 (zie vite.config.ts). Dus relative
 *   URLs werken. Alle API-paden leven onder /api zodat React-routes
 *   als /klanten/:id niet botsen met de API.
 * - In productie serveert dezelfde Render-dienst backend én frontend
 *   dus relative URLs wijzen naar dezelfde origin.
 */
const BASE_URL = "";

/** Queue voor requests die wachten op token refresh. */
type WachtendeRequest = {
  resolve: (token: string | null) => void;
  reject: (fout: unknown) => void;
};
let refreshBezig = false;
let refreshWachtrij: WachtendeRequest[] = [];

function spoelWachtrij(fout: unknown | null, token: string | null) {
  refreshWachtrij.forEach(({ resolve, reject }) => {
    if (fout) reject(fout);
    else resolve(token);
  });
  refreshWachtrij = [];
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { Accept: "application/json" },
});

// ── Client-meetpunten (storingsronde 18-08, deel C) ──────────────────
// "Het hangt bij JP" is voortaan meetbaar: elke mislukte of trage
// schrijfactie gaat fire-and-forget naar /api/ui-metingen (daar wordt
// hij een meet-event). Nooit inhoud — alleen de geanonimiseerde route,
// methode, duur en status.

const UI_METING_PAD = "/api/ui-metingen";
const UI_TRAAG_DREMPEL_MS = 2000;
const MUTATIE_METHODES = new Set(["post", "put", "patch", "delete"]);

/** Route anonimiseren: querystring weg, UUID's en getallen → :id. */
function meetRoute(url: string): string {
  return url
    .split("?")[0]
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      ":id",
    )
    .replace(/\/\d+(?=\/|$)/g, "/:id")
    .slice(0, 200);
}

function isMeetbareActie(config?: InternalAxiosRequestConfig): boolean {
  if (!config?.url || !config.method) return false;
  if (!MUTATIE_METHODES.has(config.method.toLowerCase())) return false;
  // Nooit onszelf of de auth-flow meten (geen lussen, geen ruis).
  if (config.url.startsWith(UI_METING_PAD)) return false;
  if (config.url.startsWith("/api/auth/")) return false;
  return true;
}

export function meldUiActie(
  gebeurtenis: "ui_actie_mislukt" | "ui_actie_traag",
  config: InternalAxiosRequestConfig,
  status: number | null,
): void {
  try {
    const start = (config as { _startTijd?: number })._startTijd;
    void apiClient
      .post(
        UI_METING_PAD,
        {
          gebeurtenis,
          route: meetRoute(config.url ?? ""),
          methode: (config.method ?? "").toLowerCase(),
          duur_ms: start ? Math.max(0, Date.now() - start) : 0,
          status,
        },
        { timeout: 5000 },
      )
      .catch(() => {
        // Meten is nooit blokkerend en nooit zichtbaar.
      });
  } catch {
    // Zelfde regel: een meetfout mag nooit iets raken.
  }
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = huidigAccessToken();
  if (token && !config.headers["Authorization"]) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  (config as { _startTijd?: number })._startTijd = Date.now();
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    // Trage schrijfactie (>2s): meetbaar maken (deel C, 18-08).
    const config = response.config as InternalAxiosRequestConfig & {
      _startTijd?: number;
    };
    if (isMeetbareActie(config) && config._startTijd) {
      const duur = Date.now() - config._startTijd;
      if (duur > UI_TRAAG_DREMPEL_MS) {
        meldUiActie("ui_actie_traag", config, response.status);
      }
    }
    return response;
  },
  async (fout: AxiosError) => {
    const origineel = fout.config as
      | (InternalAxiosRequestConfig & { _herprobeerd?: boolean })
      | undefined;

    // Mislukte schrijfactie meetbaar maken (deel C, 18-08). Een 401
    // niet: die loopt de refresh-flow in en is geen actie-fout.
    if (
      origineel &&
      isMeetbareActie(origineel) &&
      fout.response?.status !== 401
    ) {
      meldUiActie(
        "ui_actie_mislukt",
        origineel,
        fout.response?.status ?? null,
      );
    }

    // Toetsaccount (sessie 77): een geweigerde actie geeft één vaste
    // melding, hier op één plek voor elk scherm. Het eigen meetpunt
    // blijft stil: dat is geen actie van de gebruiker.
    if (
      isToetsWeigering(fout) &&
      !origineel?.url?.startsWith(UI_METING_PAD)
    ) {
      meldToetsWeigering();
    }

    // Alleen 401 triggert refresh; en niet als het een refresh-call zelf was
    const isRefreshCall = origineel?.url?.includes("/api/auth/refresh");
    if (
      fout.response?.status !== 401 ||
      !origineel ||
      origineel._herprobeerd ||
      isRefreshCall
    ) {
      return Promise.reject(fout);
    }

    const refresh = huidigRefreshToken();
    if (!refresh) {
      // Multi-tab (27-07): een tabblad zonder sessie (vers tabblad,
      // al uitgelogd) mag de gedeelde opslag niet aanraken — geen
      // logoutLokaal, want die zou ook andere tabbladen (en een
      // lopende 2FA-flow) raken. Had dit tabblad wél een access-token
      // maar geen refresh-token (kapotte halve staat), dan sluiten we
      // de sessie alsnog netjes af zodat het inlogscherm verschijnt.
      if (huidigAccessToken()) {
        useAuthStore.getState().logoutLokaal();
      }
      return Promise.reject(fout);
    }

    origineel._herprobeerd = true;

    if (refreshBezig) {
      // Wacht tot de lopende refresh klaar is, dan opnieuw met nieuw token
      return new Promise((resolve, reject) => {
        refreshWachtrij.push({
          resolve: (token) => {
            if (!token) return reject(fout);
            origineel.headers["Authorization"] = `Bearer ${token}`;
            resolve(apiClient.request(origineel));
          },
          reject,
        });
      });
    }

    refreshBezig = true;
    try {
      const { data } = await apiClient.post<TokenAntwoord>("/api/auth/refresh", {
        refresh_token: refresh,
      });
      useAuthStore.getState().setTokens(data.access_token, data.refresh_token);
      spoelWachtrij(null, data.access_token);
      origineel.headers["Authorization"] = `Bearer ${data.access_token}`;
      return apiClient.request(origineel);
    } catch (refreshFout) {
      // Multi-tab (27-07): heeft een ánder tabblad intussen geroteerd
      // (nieuw token via storage-sync binnengekomen), dan is de sessie
      // gewoon nog geldig — herprobeer met de verse tokens in plaats
      // van uit te loggen.
      const versRefresh = huidigRefreshToken();
      const versAccess = huidigAccessToken();
      if (versRefresh && versRefresh !== refresh && versAccess) {
        spoelWachtrij(null, versAccess);
        origineel.headers["Authorization"] = `Bearer ${versAccess}`;
        return apiClient.request(origineel);
      }
      spoelWachtrij(refreshFout, null);
      // Alleen uitloggen als de server het refresh-token echt afwees
      // (4xx: ingetrokken/verlopen). Een netwerkfout of 5xx is geen
      // bewijs van een dode sessie en mag niemand uitloggen.
      const refreshStatus = (refreshFout as AxiosError).response?.status;
      if (refreshStatus && refreshStatus >= 400 && refreshStatus < 500) {
        useAuthStore.getState().logoutLokaal();
      }
      return Promise.reject(refreshFout);
    } finally {
      refreshBezig = false;
    }
  },
);

// ───────── API functies (typed wrappers) ─────────

export const authApi = {
  async login(invoer: LoginAanvraag): Promise<LoginStap1Antwoord> {
    const { data } = await apiClient.post<LoginStap1Antwoord>(
      "/api/auth/login",
      invoer,
    );
    return data;
  },

  async valideer2FA(invoer: LoginTweeFAAanvraag): Promise<TokenAntwoord> {
    const { data } = await apiClient.post<TokenAntwoord>("/api/auth/2fa", invoer);
    return data;
  },

  async logout(refresh_token: string): Promise<void> {
    await apiClient.post("/api/auth/logout", { refresh_token });
  },

  async setup2FA(stepUp?: {
    wachtwoord?: string;
    totp_code?: string;
  }): Promise<TweeFASetupAntwoord> {
    const { data } = await apiClient.post<TweeFASetupAntwoord>(
      "/api/auth/2fa/setup",
      stepUp ?? {},
    );
    return data;
  },

  async me(): Promise<GebruikerAntwoord> {
    const { data } = await apiClient.get<GebruikerAntwoord>("/api/auth/me");
    return data;
  },

  /** P5: vraag een wachtwoord-reset mail. Server geeft altijd 204
   *  (geen enumeration). */
  async wachtwoordVergeten(email: string): Promise<void> {
    await apiClient.post("/api/auth/wachtwoord-vergeten", { email });
  },

  /** P5: zet nieuw wachtwoord met token uit reset-mail. */
  async wachtwoordReset(token: string, nieuw_wachtwoord: string): Promise<void> {
    await apiClient.post("/api/auth/wachtwoord-reset", {
      token,
      nieuw_wachtwoord,
    });
  },

  async zetAvatar(dataUrl: string): Promise<GebruikerAntwoord> {
    const { data } = await apiClient.put<GebruikerAntwoord>(
      "/api/auth/me/avatar",
      { data_url: dataUrl },
    );
    return data;
  },

  async verwijderAvatar(): Promise<GebruikerAntwoord> {
    const { data } = await apiClient.delete<GebruikerAntwoord>(
      "/api/auth/me/avatar",
    );
    return data;
  },

  async zetHandtekening(
    html: string | null,
    tekst: string | null,
  ): Promise<GebruikerAntwoord> {
    const { data } = await apiClient.put<GebruikerAntwoord>(
      "/api/auth/me/handtekening",
      { handtekening_html: html, handtekening_tekst: tekst },
    );
    return data;
  },
};

/** Globaal zoeken voor de ⌘K-palette (orders-in-beeld): het endpoint
 *  herkent zelf of de term een ordernummer, e-mailadres, telefoonnummer,
 *  postcode of naam is. */
export const zoekApi = {
  async globaal(term: string): Promise<ZoekAntwoord> {
    const { data } = await apiClient.get<ZoekAntwoord>("/api/zoek", {
      params: { term },
    });
    return data;
  },

  /** Adressuggesties voor het Aan/CC/BCC-veld (Nieuwe mail-venster):
   *  klanten en bekende contacten die matchen op naam of mailadres. */
  async adressen(term: string): Promise<AdresSuggestieAntwoord> {
    const { data } = await apiClient.get<AdresSuggestieAntwoord>(
      "/api/zoek/adressen",
      { params: { term } },
    );
    return data;
  },
};

/** Orders-menupagina (Trengo-niveau, 01-08): alle bestellingen
 *  doorzoekbaar, met filter op status en periode. */
export const ordersApi = {
  async overzicht(params: {
    zoekterm?: string;
    status?: string;
    periode_dagen?: number;
    pagina?: number;
    per_pagina?: number;
  }): Promise<OrdersOverzichtAntwoord> {
    const { data } = await apiClient.get<OrdersOverzichtAntwoord>(
      "/api/orders",
      { params },
    );
    return data;
  },
};

export const klantenApi = {
  async lijst(params: {
    zoekterm?: string;
    pagina?: number;
    per_pagina?: number;
  }): Promise<KlantLijstAntwoord> {
    const { data } = await apiClient.get<KlantLijstAntwoord>("/api/klanten", {
      params,
    });
    return data;
  },

  async dossier(klantId: string): Promise<KlantDossierAntwoord> {
    const { data } = await apiClient.get<KlantDossierAntwoord>(
      `/api/klanten/${klantId}`,
    );
    return data;
  },

  async wijzig(
    klantId: string,
    invoer: KlantWijzigen,
  ): Promise<KlantDossierAntwoord> {
    const { data } = await apiClient.patch<KlantDossierAntwoord>(
      `/api/klanten/${klantId}`,
      invoer,
    );
    return data;
  },

  async syncLightspeed(klantId: string): Promise<KlantDossierAntwoord> {
    const { data } = await apiClient.post<KlantDossierAntwoord>(
      `/api/klanten/${klantId}/sync-lightspeed`,
    );
    return data;
  },

  /** Persoonsrelaties (Vicky-case 03-08): vermoedelijk dezelfde persoon. */
  async relaties(klantId: string): Promise<KlantRelatiesAntwoord> {
    const { data } = await apiClient.get<KlantRelatiesAntwoord>(
      `/api/klanten/${klantId}/relaties`,
    );
    return data;
  },

  async relatieBevestig(
    klantId: string,
    relatieId: string,
  ): Promise<KlantRelatiesAntwoord> {
    const { data } = await apiClient.post<KlantRelatiesAntwoord>(
      `/api/klanten/${klantId}/relaties/${relatieId}/bevestig`,
    );
    return data;
  },

  async relatieAfwijzen(
    klantId: string,
    relatieId: string,
  ): Promise<KlantRelatiesAntwoord> {
    const { data } = await apiClient.post<KlantRelatiesAntwoord>(
      `/api/klanten/${klantId}/relaties/${relatieId}/afwijzen`,
    );
    return data;
  },

  /** Verrijkte klantvelden met bronlabel (klantmatching 04-08). */
  async veldVerrijkingen(klantId: string): Promise<KlantVeldVerrijking[]> {
    const { data } = await apiClient.get<KlantVeldVerrijking[]>(
      `/api/klanten/${klantId}/veld-verrijkingen`,
    );
    return data;
  },

  async veldVerrijkingTerugdraaien(
    klantId: string,
    verrijkingId: string,
  ): Promise<KlantVeldVerrijking[]> {
    const { data } = await apiClient.post<KlantVeldVerrijking[]>(
      `/api/klanten/${klantId}/veld-verrijkingen/${verrijkingId}/terugdraai`,
    );
    return data;
  },

  async orders(klantId: string): Promise<OrderAntwoord[]> {
    const { data } = await apiClient.get<OrderAntwoord[]>(
      `/api/klanten/${klantId}/orders`,
    );
    return data;
  },

  /** Fixronde 22-07: zoek ontbrekende trackingcodes langs lokaal →
   *  Picqer → TAP. Externe calls, dus alleen op verzoek. */
  async trackingZoeken(
    klantId: string,
    opnieuw = false,
  ): Promise<TrackingZoekAntwoord> {
    const { data } = await apiClient.post<TrackingZoekAntwoord>(
      `/api/klanten/${klantId}/orders/tracking-zoeken`,
      null,
      { params: { opnieuw } },
    );
    return data;
  },

  /** Fixronde 22-07: productregels van één order (variant + aantal). */
  async orderregels(
    klantId: string,
    orderId: string,
  ): Promise<OrderRegelsAntwoord> {
    const { data } = await apiClient.get<OrderRegelsAntwoord>(
      `/api/klanten/${klantId}/orders/${orderId}/regels`,
    );
    return data;
  },

  /** Ronde 4A: zendingen van een order met verse Picqer-status en live
   *  vervoerderstatus. `ververs` (de knop) omzeilt de korte
   *  vervoerderstatus-cache op de server. */
  async zendingen(
    klantId: string,
    orderId: string,
    opties?: { ververs?: boolean },
  ): Promise<ZendingAntwoord[]> {
    const { data } = await apiClient.get<ZendingAntwoord[]>(
      `/api/klanten/${klantId}/orders/${orderId}/zendingen`,
      opties?.ververs ? { params: { ververs: true } } : undefined,
    );
    return data;
  },

  /** Doorloop 06-08: reviews van deze klant, rechtstreeks uit
   *  review_raw op klant_id (gekoppeld via het blind-indexpad). */
  async reviews(klantId: string): Promise<KlantReview[]> {
    const { data } = await apiClient.get<KlantReview[]>(
      `/api/klanten/${klantId}/reviews`,
    );
    return data;
  },

  /** Klantkaart v2 (07-08): retouren van deze klant voor het
   *  retourenblok (compact, uit de eigen retouren-tabel). */
  async retouren(klantId: string): Promise<KlantRetour[]> {
    const { data } = await apiClient.get<KlantRetour[]>(
      `/api/klanten/${klantId}/retouren`,
    );
    return data;
  },

  // Fase 1 — volledig dossier inclusief order_summary + verrijking + reviews
  async dossierVolledig(
    klantId: string,
    staleThresholdUren: number = 24,
  ): Promise<KlantDossierVolledig> {
    const { data } = await apiClient.get<KlantDossierVolledig>(
      `/api/klanten/${klantId}/dossier`,
      { params: { stale_threshold_uren: staleThresholdUren } },
    );
    return data;
  },

  // Trigger lazy refresh vanuit Lightspeed (alleen als stale of forceer=true)
  async lazyRefresh(
    klantId: string,
    forceer: boolean = false,
  ): Promise<SyncStatusAntwoord> {
    const { data } = await apiClient.post<SyncStatusAntwoord>(
      `/api/klanten/${klantId}/lazy-refresh`,
      undefined,
      { params: { forceer } },
    );
    return data;
  },

  // Lichte lookup op email-adres (case-insensitive, ook alt-adressen)
  async byEmail(email: string): Promise<KlantAntwoord> {
    const { data } = await apiClient.get<KlantAntwoord>(
      `/api/klanten/by-email/${encodeURIComponent(email)}`,
    );
    return data;
  },
};

export const huisdierenApi = {
  async lijst(klantId: string): Promise<HuisdierAntwoord[]> {
    const { data } = await apiClient.get<HuisdierAntwoord[]>(
      `/api/klanten/${klantId}/huisdieren`,
    );
    return data;
  },

  async maak(
    klantId: string,
    invoer: HuisdierAanmaken,
  ): Promise<HuisdierAntwoord> {
    const { data } = await apiClient.post<HuisdierAntwoord>(
      `/api/klanten/${klantId}/huisdieren`,
      invoer,
    );
    return data;
  },

  async wijzig(
    huisdierId: string,
    invoer: HuisdierWijzigen,
  ): Promise<HuisdierAntwoord> {
    const { data } = await apiClient.patch<HuisdierAntwoord>(
      `/api/huisdieren/${huisdierId}`,
      invoer,
    );
    return data;
  },

  async verwijder(huisdierId: string): Promise<void> {
    await apiClient.delete(`/api/huisdieren/${huisdierId}`);
  },

  /** Huisdieren mét verse aanname-engine-inschatting (live berekend),
   *  plus de orderanalyse voor een eerlijke lege staat (22-07). */
  async dierinschatting(klantId: string): Promise<DierinschattingAntwoord> {
    const { data } = await apiClient.get<DierinschattingAntwoord>(
      `/api/klanten/${klantId}/dierinschatting`,
    );
    return data;
  },

  /** Vervang de allergielijst (replace-all; handmatig = bevestigd). */
  async zetAllergieen(
    huisdierId: string,
    allergieen: AllergieInvoer[],
  ): Promise<HuisdierAntwoord> {
    const { data } = await apiClient.put<HuisdierAntwoord>(
      `/api/huisdieren/${huisdierId}/allergieen`,
      { allergieen },
    );
    return data;
  },

  /** Deterministisch combinatievoorstel (fase 2a); ná dierinschatting
   *  aanroepen zodat de engine-payload vers is. */
  async combinatievoorstel(
    huisdierId: string,
  ): Promise<CombinatievoorstelAntwoord> {
    const { data } = await apiClient.get<CombinatievoorstelAntwoord>(
      `/api/huisdieren/${huisdierId}/combinatievoorstel`,
    );
    return data;
  },

  async adviesregels(huisdierId: string): Promise<AdviesregelAntwoord[]> {
    const { data } = await apiClient.get<AdviesregelAntwoord[]>(
      `/api/huisdieren/${huisdierId}/adviesregels`,
    );
    return data;
  },

  /** Knop "Advies vastleggen": bewaar invoer + uitkomst als regel. */
  async legAdviesVast(
    huisdierId: string,
    invoer: Record<string, unknown>,
    uitkomst: Record<string, unknown>,
  ): Promise<AdviesregelAntwoord> {
    const { data } = await apiClient.post<AdviesregelAntwoord>(
      `/api/huisdieren/${huisdierId}/adviesregels`,
      { invoer, uitkomst },
    );
    return data;
  },

  /** Knop "Meerdere dieren": splits een aanname in 2+ bevestigde
   *  profielen. */
  async splitsen(
    huisdierId: string,
    profielen: HuisdierAanmaken[],
  ): Promise<HuisdierAntwoord[]> {
    const { data } = await apiClient.post<HuisdierAntwoord[]>(
      `/api/huisdieren/${huisdierId}/splitsen`,
      { profielen },
    );
    return data;
  },
};

export const adviesApi = {
  async rekentoolConfig(): Promise<RekentoolConfig> {
    const { data } = await apiClient.get<RekentoolConfig>(
      "/api/advies/rekentool-config",
    );
    return data;
  },

  async rekentool(aanvraag: RekentoolAanvraag): Promise<RekentoolResultaat> {
    const { data } = await apiClient.post<RekentoolResultaat>(
      "/api/advies/rekentool",
      aanvraag,
    );
    return data;
  },
};

export const gebruikersApi = {
  async lijst(): Promise<GebruikerAntwoord[]> {
    const { data } = await apiClient.get<GebruikerAntwoord[]>("/api/gebruikers");
    return data;
  },

  async maak(invoer: GebruikerAanmaken): Promise<GebruikerAntwoord> {
    const { data } = await apiClient.post<GebruikerAntwoord>(
      "/api/gebruikers",
      invoer,
    );
    return data;
  },

  async wijzig(
    gebruikerId: string,
    invoer: GebruikerWijzigen,
  ): Promise<GebruikerAntwoord> {
    const { data } = await apiClient.patch<GebruikerAntwoord>(
      `/api/gebruikers/${gebruikerId}`,
      invoer,
    );
    return data;
  },

  async deactiveer(gebruikerId: string): Promise<void> {
    await apiClient.delete(`/api/gebruikers/${gebruikerId}`);
  },
};

export const avgApi = {
  async exporteer(klantId: string): Promise<AvgExportAntwoord> {
    const { data } = await apiClient.post<AvgExportAntwoord>(
      `/api/admin/avg/export/${klantId}`,
    );
    return data;
  },

  downloadUrl(exportId: string): string {
    return `/api/admin/avg/export/${exportId}`;
  },

  async voorbereiding(klantId: string): Promise<AvgVoorbereiding> {
    const { data } = await apiClient.get<AvgVoorbereiding>(
      `/api/admin/avg/voorbereiding/${klantId}`,
    );
    return data;
  },

  async anonimiseer(
    klantId: string,
    bevestiging: AvgAnonimiseerBevestiging,
  ): Promise<AvgAnonimiseerUitkomst> {
    const { data } = await apiClient.post<AvgAnonimiseerUitkomst>(
      `/api/admin/avg/anonimiseer/${klantId}`,
      bevestiging,
    );
    return data;
  },
};

// ────── E-mails ──────

export interface EmailLijstParams {
  tab?: EmailTabId;
  categorie?: EmailCategorie | "alle";
  zoekterm?: string;
  klant_id?: string;
  mailbox_id?: string;
  /** Mailronde 06-08: alleen gesprekken met dit label. */
  label_id?: string;
  pagina?: number;
  per_pagina?: number;
}

/** Vrije labels op mailgesprekken (mailronde 06-08). Toekennen en
 *  weghalen geven de actuele labels van het gesprek terug zodat de UI
 *  de rij direct kan bijwerken. */
export const mailLabelsApi = {
  async lijst(): Promise<MailLabelAntwoord[]> {
    const { data } =
      await apiClient.get<MailLabelAntwoord[]>("/api/mail-labels");
    return data;
  },
  async maakAan(naam: string): Promise<MailLabelAntwoord> {
    const { data } = await apiClient.post<MailLabelAntwoord>(
      "/api/mail-labels",
      { naam },
    );
    return data;
  },
  async verwijder(labelId: string): Promise<void> {
    await apiClient.delete(`/api/mail-labels/${labelId}`);
  },
  async vanEmail(emailId: string): Promise<MailLabelKort[]> {
    const { data } = await apiClient.get<MailLabelKort[]>(
      `/api/mail-labels/emails/${emailId}`,
    );
    return data;
  },
  async kenToe(labelId: string, emailId: string): Promise<MailLabelKort[]> {
    const { data } = await apiClient.post<MailLabelKort[]>(
      `/api/mail-labels/${labelId}/emails/${emailId}`,
    );
    return data;
  },
  async haalWeg(labelId: string, emailId: string): Promise<MailLabelKort[]> {
    const { data } = await apiClient.delete<MailLabelKort[]>(
      `/api/mail-labels/${labelId}/emails/${emailId}`,
    );
    return data;
  },
};

/** Antwoordsjablonen (mailronde 06-08): lijst voor de composer-kiezer,
 *  CRUD voor de beheerpagina. */
export const sjablonenApi = {
  async lijst(): Promise<SjabloonAntwoord[]> {
    const { data } =
      await apiClient.get<SjabloonAntwoord[]>("/api/sjablonen");
    return data;
  },
  async beheerLijst(): Promise<SjabloonAntwoord[]> {
    const { data } =
      await apiClient.get<SjabloonAntwoord[]>("/api/admin/sjablonen");
    return data;
  },
  async maakAan(invoer: {
    titel: string;
    categorie?: string | null;
    inhoud: string;
    volgorde?: number;
    actief?: boolean;
  }): Promise<SjabloonAntwoord> {
    const { data } = await apiClient.post<SjabloonAntwoord>(
      "/api/admin/sjablonen",
      invoer,
    );
    return data;
  },
  async wijzig(
    id: string,
    invoer: Partial<{
      titel: string;
      categorie: string | null;
      inhoud: string;
      volgorde: number;
      actief: boolean;
    }>,
  ): Promise<SjabloonAntwoord> {
    const { data } = await apiClient.patch<SjabloonAntwoord>(
      `/api/admin/sjablonen/${id}`,
      invoer,
    );
    return data;
  },
  async verwijder(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/sjablonen/${id}`);
  },
};

/** Publieke mail-CSAT-endpoints (mailronde 06-08): tokenlink uit de
 *  mail, geen inlog. POST zodat het token niet in access-logs landt. */
export const mailCsatApi = {
  async context(
    token: string,
  ): Promise<{ al_ingevuld: boolean; score: number | null }> {
    const { data } = await apiClient.post<{
      al_ingevuld: boolean;
      score: number | null;
    }>("/api/mail-csat/context", { token });
    return data;
  },
  async stuurScore(
    token: string,
    score: number,
    toelichting?: string,
  ): Promise<{ ok: boolean }> {
    const { data } = await apiClient.post<{ ok: boolean }>(
      "/api/mail-csat/score",
      { token, score, toelichting: toelichting || null },
    );
    return data;
  },
};

/** Publieke herbestelpagina (herbestelronde 15-08): tokenlink of
 *  shopklant-token, geen inlog. POST zodat tokens niet in access-logs
 *  landen (zelfde huisregel als mail-CSAT). */
// De wachtstand van het eenscherm mag nooit stil blijven hangen: tien
// seconden, dan één stille herpoging en pas daarna de melding "Dat
// lukte even niet" met een knop Opnieuw proberen (mock 15-09). Bewust
// korter dan de 30 s van de betaalstart — dit is een verversing tijdens
// het invullen, geen lopende betaling.
//
// Waarom tien en niet acht (15-09, tweede ronde): een verse offerte
// kost op productie gemeten ruim vijf seconden (checkout aanmaken,
// producten erin, opties lezen, verzend- en betaalmethode zetten,
// valideren, teruglezen), en elk extra product in het mandje komt daar
// een kwart seconde bij. Acht seconden was daarmee krap genoeg om bij
// een groot mandje af te kappen op een verzoek dat het gewoon deed.
export const HERBESTEL_OFFERTE_TIMEOUT_MS = 10_000;

export const herbestelApi = {
  async pagina(
    token: string | null,
    shopToken: string | null,
  ): Promise<HerbestelPaginaAntwoord> {
    const { data } = await apiClient.post<HerbestelPaginaAntwoord>(
      "/api/public/herbestel/pagina",
      { token, shop_token: shopToken },
    );
    return data;
  },
  async afrekenen(
    token: string | null,
    shopToken: string | null,
    regels: { vid: string; aantal: number }[],
    combidealVid: string | null,
  ): Promise<HerbestelAfrekenenAntwoord> {
    const { data } = await apiClient.post<HerbestelAfrekenenAntwoord>(
      "/api/public/herbestel/afrekenen",
      {
        token,
        shop_token: shopToken,
        regels,
        combideal_vid: combidealVid,
      },
    );
    return data;
  },
  async stand(): Promise<HerbestelStandAntwoord> {
    const { data } = await apiClient.get<HerbestelStandAntwoord>(
      "/api/public/herbestel/stand",
    );
    return data;
  },
  // Antwoordt altijd hetzelfde, ook voor een onbekend adres — de
  // bevestiging op het scherm hangt dus niet van het antwoord af.
  // Sinds de veiligheidsronde V1 (A3) zegt het antwoord wél of dit
  // adres zijn vierde poging van het etmaal heeft gehad; de pagina
  // toont dan één neutrale tekst met de verwijzing naar de
  // klantenservice (de teller telt per adres, bekend of onbekend).
  async aanvraag(email: string): Promise<HerbestelAanvraagAntwoord> {
    const { data } = await apiClient.post<HerbestelAanvraagAntwoord>(
      "/api/public/herbestel/aanvraag",
      { email },
    );
    return data;
  },
  // Eenscherm 15-09: de offerte van het mandje. Bij het openen en na
  // elke wijziging van aantal, adres, bezorgkeuze, betaalmethode of
  // kortingscode. Alles wat het scherm toont komt hieruit — het
  // portaal rekent niets. Acht seconden is de harde grens uit de mock:
  // daarboven toont de pagina "Dat lukte even niet" met een knop
  // Opnieuw proberen, en de betaalknop blijft uit.
  async offerte(
    token: string | null,
    shopToken: string | null,
    invoer: {
      regels: { vid: string; aantal: number }[];
      combidealVid: string | null;
      adresKeuze: "klant" | "ander";
      adres: HerbestelAnderAdres | null;
      methode: string;
      bezorgoptie: string | null;
      // Hotfix 17-09: de titel van de gekozen vervoerder. Het
      // shipment_id hoort bij één gewichtsklasse en verdwijnt zodra
      // het mandje verandert; hiermee houdt de server de bewuste
      // keuze vast.
      bezorgvervoerder: string | null;
      kortingscode: string | null;
    },
    signal?: AbortSignal,
  ): Promise<HerbestelOfferteAntwoord> {
    const { data } = await apiClient.post<HerbestelOfferteAntwoord>(
      "/api/public/herbestel/offerte",
      {
        token,
        shop_token: shopToken,
        regels: invoer.regels,
        combideal_vid: invoer.combidealVid,
        adres_keuze: invoer.adresKeuze,
        adres: invoer.adres,
        methode: invoer.methode,
        bezorgoptie: invoer.bezorgoptie,
        bezorgvervoerder: invoer.bezorgvervoerder,
        kortingscode: invoer.kortingscode,
      },
      { timeout: HERBESTEL_OFFERTE_TIMEOUT_MS, signal },
    );
    return data;
  },
  // Betaalflow (17-08, feature flag ideal_afrekenen). Start de
  // betaling met de op de pagina gekozen methode; het antwoord is
  // alleen de MSP-betaal-URL.
  async betalen(
    token: string | null,
    shopToken: string | null,
    regels: { vid: string; aantal: number }[],
    combidealVid: string | null,
    adresKeuze: "klant" | "ander",
    adres: HerbestelAnderAdres | null,
    methode: string,
    // Eenscherm 15-09: de bezorgkeuze, de kortingscode en de
    // vingerafdruk van het bedrag dat op het scherm stond. De server
    // weigert (409) zodra de verse offerte een ander bedrag geeft.
    extra?: {
      bezorgoptie: string | null;
      bezorgvervoerder?: string | null;
      kortingscode: string | null;
      offerteAfdruk: string | null;
    },
  ): Promise<HerbestelBetalenAntwoord> {
    const { data } = await apiClient.post<HerbestelBetalenAntwoord>(
      "/api/public/herbestel/betalen",
      {
        token,
        shop_token: shopToken,
        regels,
        combideal_vid: combidealVid,
        adres_keuze: adresKeuze,
        adres,
        methode,
        bezorgoptie: extra?.bezorgoptie ?? null,
        bezorgvervoerder: extra?.bezorgvervoerder ?? null,
        kortingscode: extra?.kortingscode ?? null,
        offerte_afdruk: extra?.offerteAfdruk ?? null,
      },
      // Sinds de snelle betaalstart (17-08, avond) doet de server
      // alleen nog de MSP-order; de ruime grens blijft als vangnet
      // zodat een trage externe dienst een geldige start nooit
      // halverwege afkapt.
      { timeout: 30_000 },
    );
    return data;
  },
  // Meet-event "betaalmethode gekozen" (meetronde 17-08): puur
  // meten, fire-and-forget — de aanroeper wacht er nooit op en een
  // fout mag de betaalflow nooit raken.
  async meetMethode(
    token: string | null,
    shopToken: string | null,
    methode: string,
    voorselectie = false,
  ): Promise<void> {
    await apiClient.post("/api/public/herbestel/meet/methode", {
      token,
      shop_token: shopToken,
      methode,
      voorselectie,
    });
  },
  // Meet-events van de stapwissel (tweestaps-ronde 17-08): net als
  // meetMethode puur meten en fire-and-forget.
  async meetStap(
    token: string | null,
    shopToken: string | null,
    stap: HerbestelMeetStap,
  ): Promise<void> {
    await apiClient.post("/api/public/herbestel/meet/stap", {
      token,
      shop_token: shopToken,
      stap,
    });
  },
  // Sessie 23: de typfoutlijst voor het e-mailveld komt uit de publieke
  // shop-extras-config (één bron met de checkout). Faalt de fetch, dan
  // is er gewoon geen suggestie.
  async emailTypfouten(): Promise<EmailTypfoutConfig | null> {
    try {
      const { data } = await apiClient.get<{
        email_typfouten?: EmailTypfoutConfig;
      }>("/api/public/shop-extras");
      return data?.email_typfouten ?? null;
    } catch {
      return null;
    }
  },
  // Sessie 23: meting van het typfout-vangnet, via dezelfde publieke
  // meetroute als de checkout (stap "herbestel"). Fire-and-forget.
  async meetEmailSuggestie(
    gebeurtenis:
      | "email_suggestie_getoond"
      | "email_suggestie_geaccepteerd"
      | "email_suggestie_genegeerd",
    domein: string,
  ): Promise<void> {
    await apiClient.post("/api/public/checkout/event", {
      gebeurtenis,
      domein,
      stap: "herbestel",
    });
  },
  // Bevestigingspagina: status via de eigen referentie (POST, zelfde
  // log-hygiëne als de tokens). De server doet zelf de MSP-fallback.
  async betalingStatus(
    referentie: string,
  ): Promise<HerbestelBetalingStatusAntwoord> {
    const { data } =
      await apiClient.post<HerbestelBetalingStatusAntwoord>(
        "/api/public/herbestel/betaling",
        { referentie },
      );
    return data;
  },
};

/** Publieke reviewstand voor het eigen WebwinkelKeur-blok (17-08).
 *  Geen auth, geen PII: hetzelfde cijfer dat op de reviewpagina van
 *  WebwinkelKeur staat. Zwaar gecachet aan de serverkant. */
export const reviewsPubliekApi = {
  async score(): Promise<ReviewsScoreAntwoord> {
    const { data } = await apiClient.get<ReviewsScoreAntwoord>(
      "/api/public/reviews-score",
    );
    return data;
  },
};

export const conversatiesApi = {
  // Ronde 2 (KEUZE 4). Haalt (of maakt) de conversatie voor een mail.
  async voorEmail(emailId: string): Promise<ConversatieDetail> {
    const { data } = await apiClient.get<ConversatieDetail>(
      `/api/conversaties/voor-email/${emailId}`,
    );
    return data;
  },
  async koppelOrder(
    conversatieId: string,
    orderId: string | null,
  ): Promise<ConversatieDetail> {
    const { data } = await apiClient.patch<ConversatieDetail>(
      `/api/conversaties/${conversatieId}/order`,
      { order_id: orderId },
    );
    return data;
  },
  async voegNotitieToe(
    conversatieId: string,
    inhoud: string,
  ): Promise<ConversatieDetail> {
    const { data } = await apiClient.post<ConversatieDetail>(
      `/api/conversaties/${conversatieId}/notities`,
      { inhoud },
    );
    return data;
  },
  async verwijderNotitie(notitieId: string): Promise<ConversatieDetail> {
    const { data } = await apiClient.delete<ConversatieDetail>(
      `/api/conversaties/notities/${notitieId}`,
    );
    return data;
  },
  // Ronde 3 (punt 4). (Her)genereer de thread-samenvatting.
  async genereerSamenvatting(
    conversatieId: string,
    force = false,
  ): Promise<ConversatieDetail> {
    const { data } = await apiClient.post<ConversatieDetail>(
      `/api/conversaties/${conversatieId}/samenvatting`,
      null,
      { params: { force } },
    );
    return data;
  },
};

export const adminHugoSleutelsApi = {
  async lijst(): Promise<HugoSleutel[]> {
    const { data } = await apiClient.get<HugoSleutel[]>(
      "/api/admin/hugo-sleutels",
    );
    return data;
  },
  async aanmaken(
    naam: string,
    scope: string = "lezen",
  ): Promise<HugoSleutelAangemaakt> {
    const { data } = await apiClient.post<HugoSleutelAangemaakt>(
      "/api/admin/hugo-sleutels",
      { naam, scope },
    );
    return data;
  },
  async intrekken(id: string): Promise<HugoSleutel> {
    const { data } = await apiClient.post<HugoSleutel>(
      `/api/admin/hugo-sleutels/${id}/intrekken`,
    );
    return data;
  },
};

export const adminChatApi = {
  /** Sidebar-tellers Chat/WhatsApp (fixronde 04-08): per kanaal het
   *  aantal niet-gesloten gesprekken met ongelezen activiteit. */
  async tellers(): Promise<ChatTellersAntwoord> {
    const { data } = await apiClient.get<ChatTellersAntwoord>(
      "/api/admin/chat/tellers",
    );
    return data;
  },
  /** Nieuw-gesprek (07-08): waar zou een WhatsApp-gesprek heen gaan en
   *  staat het 24-uursvenster open? Leesweg zonder bijwerkingen. */
  async whatsappDoel(doel: {
    klantId?: string;
    telefoonnummer?: string;
  }): Promise<WhatsAppDoelAntwoord> {
    const { data } = await apiClient.get<WhatsAppDoelAntwoord>(
      "/api/admin/chat/whatsapp-doel",
      {
        params: {
          klant_id: doel.klantId,
          telefoonnummer: doel.telefoonnummer,
        },
      },
    );
    return data;
  },
  /** Sessielijst van het Chat-werkscherm (31-07): inbox of archief.
   *  `kanaal` (kanaalronde 01-08) filtert op chat of whatsapp; zonder
   *  filter staan alle kanalen door elkaar (met badge). */
  async lijst(
    pagina = 1,
    perPagina = 25,
    weergave: AdminChatWeergave = "archief",
    kanaal?: ChatKanaal,
  ): Promise<AdminChatSessieLijst> {
    const { data } = await apiClient.get<AdminChatSessieLijst>(
      "/api/admin/chat/sessies",
      { params: { pagina, per_pagina: perPagina, weergave, kanaal } },
    );
    return data;
  },
  /** "Aan mij toewijzen": sessie op overgenomen, Nova zwijgt. */
  async neemOver(sessieId: string): Promise<void> {
    await apiClient.post(`/api/admin/chat/sessies/${sessieId}/overnemen`);
  },
  /** Gelezen-markering: het gesprek staat echt in beeld. */
  async markeerGelezen(sessieId: string): Promise<void> {
    await apiClient.post(`/api/admin/chat/sessies/${sessieId}/gelezen`);
  },
  async detail(sessieId: string): Promise<AdminChatSessieDetail> {
    const { data } = await apiClient.get<AdminChatSessieDetail>(
      `/api/admin/chat/sessies/${sessieId}`,
    );
    return data;
  },
  /** Klantmatching 04-08: chat-koppelvoorstel accepteren → INTERNE
   *  koppeling (werklijst/dossier); de identificatie-lat blijft. */
  async accepteerKoppelVoorstel(
    sessieId: string,
    voorstelId: string,
  ): Promise<AdminChatSessieDetail> {
    const { data } = await apiClient.post<AdminChatSessieDetail>(
      `/api/admin/chat/sessies/${sessieId}/koppel-voorstel/${voorstelId}/accepteer`,
    );
    return data;
  },
  async negeerKoppelVoorstel(
    sessieId: string,
    voorstelId: string,
  ): Promise<AdminChatSessieDetail> {
    const { data } = await apiClient.post<AdminChatSessieDetail>(
      `/api/admin/chat/sessies/${sessieId}/koppel-voorstel/${voorstelId}/negeer`,
    );
    return data;
  },
  /** Medewerker-bericht: sessie gaat op 'overgenomen', Nova zwijgt. */
  async stuurBericht(
    sessieId: string,
    inhoud: string,
  ): Promise<AdminChatBericht> {
    const { data } = await apiClient.post<AdminChatBericht>(
      `/api/admin/chat/sessies/${sessieId}/berichten`,
      { inhoud },
    );
    return data;
  },
  async sluit(sessieId: string): Promise<void> {
    await apiClient.post(`/api/admin/chat/sessies/${sessieId}/sluiten`);
  },
  async geefTerug(sessieId: string): Promise<void> {
    await apiClient.post(`/api/admin/chat/sessies/${sessieId}/teruggeven`);
  },
  /** Testdata-markering (om)zetten (correctie op de markeerronde). */
  async zetTestMarkering(sessieId: string, isTest: boolean): Promise<void> {
    await apiClient.post(
      `/api/admin/chat/sessies/${sessieId}/test-markering`,
      { is_test: isTest },
    );
  },
  /** Gemarkeerd-bewaren (AVG): dit gesprek blijft altijd staan. */
  async zetBewaren(sessieId: string, bewaren: boolean): Promise<void> {
    await apiClient.post(`/api/admin/chat/sessies/${sessieId}/bewaren`, {
      bewaren,
    });
  },
  /** Bijlage (foto) als blob — auth loopt via de axios-interceptor. */
  async bijlage(berichtId: string): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      `/api/admin/chat/berichten/${berichtId}/bijlage`,
      { responseType: "blob" },
    );
    return data;
  },
  /** Schrijfhulp bij overname (06-08): ruwe invoer → één kant-en-klaar
   *  chatbericht; bijsturen werkt op het huidige voorstel. Kanaal,
   *  klantcontext en gespreksgeschiedenis komen server-side van de
   *  sessie. */
  async schrijfhulp(
    sessieId: string,
    vraag: {
      modus: SchrijfhulpModus;
      invoer?: string;
      huidige_tekst?: string;
      aanwijzing?: string;
    },
  ): Promise<ChatSchrijfhulpAntwoord> {
    const { data } = await apiClient.post<ChatSchrijfhulpAntwoord>(
      `/api/admin/chat/sessies/${sessieId}/schrijfhulp`,
      vraag,
    );
    return data;
  },
};

// ── WhatsApp-templates (06-08): beheer + verzending ──────────────────

export const whatsappTemplatesApi = {
  async lijst(): Promise<WhatsAppTemplateItem[]> {
    const { data } = await apiClient.get<{ items: WhatsAppTemplateItem[] }>(
      "/api/admin/whatsapp-templates",
    );
    return data.items;
  },
  async aanmaken(body: {
    naam: string;
    categorie: string;
    body_tekst: string;
    variabele_labels: string[];
    voorbeeld_variabelen: string[];
  }): Promise<WhatsAppTemplateItem> {
    const { data } = await apiClient.post<WhatsAppTemplateItem>(
      "/api/admin/whatsapp-templates",
      body,
    );
    return data;
  },
  async bewerken(
    templateId: string,
    body: {
      naam: string;
      categorie: string;
      body_tekst: string;
      variabele_labels: string[];
      voorbeeld_variabelen: string[];
    },
  ): Promise<WhatsAppTemplateItem> {
    const { data } = await apiClient.put<WhatsAppTemplateItem>(
      `/api/admin/whatsapp-templates/${templateId}`,
      body,
    );
    return data;
  },
  async indienen(templateId: string): Promise<WhatsAppTemplateItem> {
    const { data } = await apiClient.post<WhatsAppTemplateItem>(
      `/api/admin/whatsapp-templates/${templateId}/indienen`,
    );
    return data;
  },
  async sync(): Promise<{ bijgewerkt: number; onbekend_bij_meta: number }> {
    const { data } = await apiClient.post<{
      bijgewerkt: number;
      onbekend_bij_meta: number;
    }>("/api/admin/whatsapp-templates/sync");
    return data;
  },
  /** Verzending: vanuit een gesprek (sessieId), proactief vanaf de
   *  klantkaart (klantId) of naar een los nummer (Nieuw gesprek,
   *  07-08). Kan uitsluitend met een goedgekeurde template; de
   *  bevestigingsstap zit in de dialoog. */
  async verstuur(body: {
    template_id: string;
    variabelen: string[];
    sessie_id?: string;
    klant_id?: string;
    telefoonnummer?: string;
  }): Promise<WhatsAppTemplateVerstuurd> {
    const { data } = await apiClient.post<WhatsAppTemplateVerstuurd>(
      "/api/admin/whatsapp-templates/verstuur",
      body,
    );
    return data;
  },
};

// ── Luna-schrijfacties (26-07): meldingen/werkvoorraad + audit ───────

export interface LunaWijzigingItem {
  id: string;
  created_at: string;
  soort: string;
  status: string;
  kanaal: string;
  van_waarde: string | null;
  naar_waarde: string | null;
  ordernummer: string | null;
  doel: string | null;
  resultaat_portaal: string | null;
  resultaat_lightspeed: string | null;
  resultaat_picqer: string | null;
  resultaat_notificatie: string | null;
  fout_detail: string | null;
  klant_id: string | null;
  klant_naam: string | null;
  laatste_ordernummer: string | null;
  afgehandeld_op: string | null;
}

export interface LunaWijzigingLijst {
  wijzigingen: LunaWijzigingItem[];
  open_aantal: number;
}

export const lunaWijzigingenApi = {
  async lijst(alleenOpen = false): Promise<LunaWijzigingLijst> {
    const { data } = await apiClient.get<LunaWijzigingLijst>(
      "/api/admin/luna-wijzigingen",
      { params: { alleen_open: alleenOpen } },
    );
    return data;
  },
  async markeerAfgehandeld(id: string): Promise<LunaWijzigingItem> {
    const { data } = await apiClient.post<LunaWijzigingItem>(
      `/api/admin/luna-wijzigingen/${id}/afgehandeld`,
    );
    return data;
  },
};

// ── JP-actiecentrum (27-07): één lijst voor alles wat JP moet doen ───

export type ActiecentrumCategorie = "wijziging" | "handmatig" | "wachtend";

export interface ChecklistPunt {
  tekst: string;
  afgevinkt: boolean;
}

export interface ActiecentrumItem {
  id: string;
  type: "wijziging" | "actie";
  wacht_op_jp: boolean;
  created_at: string;
  soort: string;
  klant_id: string | null;
  klant_naam: string | null;
  ordernummer: string | null;
  /** Deeplink naar de order in de Lightspeed-backoffice (fixronde 11-09). */
  lightspeed_order_url?: string | null;
  bron_email_id: string | null;
  afgehandeld_op: string | null;
  // Wijziging-detail (type === "wijziging").
  status: string | null;
  kanaal: string | null;
  van_waarde: string | null;
  naar_waarde: string | null;
  doel: string | null;
  resultaat_portaal: string | null;
  resultaat_lightspeed: string | null;
  resultaat_picqer: string | null;
  resultaat_notificatie: string | null;
  fout_detail: string | null;
  laatste_ordernummer: string | null;
  // Actie-detail (type === "actie").
  titel: string | null;
  omschrijving: string | null;
  checklist: ChecklistPunt[] | null;
  blokkeert_concept: boolean;
  /** Betaalafstemming (tweede ronde 14-09): hierop staat de knop
   *  "Verklaard" — een afwijking die echt is maar geen storing. */
  verklaarbaar?: boolean;
  verklaard_reden?: string | null;
}

export interface ActiecentrumTellingen {
  open_totaal: number;
  open_wijzigingen: number;
  open_handmatig: number;
  open_wachtend: number;
}

export interface ActiecentrumLijst {
  items: ActiecentrumItem[];
  tellingen: ActiecentrumTellingen;
}

export const actiecentrumApi = {
  /** Alleen de aantallen, voor de teller in de zijbalk (hotfix 14-09).
   *  Eigen endpoint: de lijst haalt 400 rijen op en zoekt per rij de
   *  klantnaam erbij — veel te zwaar voor iets dat overal meeloopt. */
  async tellingen(): Promise<ActiecentrumTellingen> {
    const { data } = await apiClient.get<ActiecentrumTellingen>(
      "/api/admin/actiecentrum/tellingen",
    );
    return data;
  },
  async lijst(
    categorie: ActiecentrumCategorie | null,
    alleenOpen = false,
  ): Promise<ActiecentrumLijst> {
    const { data } = await apiClient.get<ActiecentrumLijst>(
      "/api/admin/actiecentrum",
      {
        params: {
          ...(categorie ? { categorie } : {}),
          alleen_open: alleenOpen,
        },
      },
    );
    return data;
  },
  async actieAfgehandeld(id: string): Promise<ActiecentrumItem> {
    const { data } = await apiClient.post<ActiecentrumItem>(
      `/api/admin/actiecentrum/acties/${id}/afgehandeld`,
    );
    return data;
  },
  async vinkChecklistpunt(
    id: string,
    index: number,
    afgevinkt: boolean,
  ): Promise<ActiecentrumItem> {
    const { data } = await apiClient.post<ActiecentrumItem>(
      `/api/admin/actiecentrum/acties/${id}/checklist/${index}`,
      { afgevinkt },
    );
    return data;
  },
  /** Zet een afstemmingsafwijking weg als verklaard, met reden. Latere
   *  afstemmingsrondes tellen hem wel maar melden hem niet meer. */
  async actieVerklaard(id: string, reden: string): Promise<ActiecentrumItem> {
    const { data } = await apiClient.post<ActiecentrumItem>(
      `/api/admin/actiecentrum/acties/${id}/verklaard`,
      { reden },
    );
    return data;
  },
};

export const dashboardApi = {
  /** Hoofddashboard v2 (07-08): aandacht, kanalen, tevredenheid, systeem. */
  async hoofd(): Promise<HoofdDashboard> {
    const { data } = await apiClient.get<HoofdDashboard>(
      "/api/dashboard/hoofd",
    );
    return data;
  },
  /** E-maildashboard (07-08): mail-KPI's, lopende afspraken, vandaag. */
  async email(): Promise<EmailDashboard> {
    const { data } = await apiClient.get<EmailDashboard>(
      "/api/dashboard/email",
    );
    return data;
  },
  /** Klantniveau-herbesteltrechter (meetronde 17-08), los van /hoofd
   *  zodat een filterwissel niet alle blokken opnieuw bouwt. */
  async herbestelTrechter(params: {
    dagen: number;
    bron?: string;
    apparaat?: string;
  }): Promise<HerbestelTrechterAntwoord> {
    const { data } = await apiClient.get<HerbestelTrechterAntwoord>(
      "/api/dashboard/herbestel-trechter",
      { params },
    );
    return data;
  },
  /** Doorklik van één trechterstap naar de klantniveau-lijst. */
  async herbestelTrechterKlanten(params: {
    stap: string;
    dagen: number;
    bron?: string;
    apparaat?: string;
  }): Promise<HerbestelTrechterKlantenAntwoord> {
    const { data } =
      await apiClient.get<HerbestelTrechterKlantenAntwoord>(
        "/api/dashboard/herbestel-trechter/klanten",
        { params },
      );
    return data;
  },
};

/** Storingslogboek (storingsronde 18-08): lijst + dag/kanaal/reden-tellers. */
export const storingenApi = {
  async lijst(dagen = 30): Promise<StoringenAntwoord> {
    const { data } = await apiClient.get<StoringenAntwoord>(
      "/api/admin/storingen",
      { params: { dagen } },
    );
    return data;
  },
};

export const recoveryMailsApi = {
  async overzicht(dagen = 30): Promise<RecoveryOverzichtAntwoord> {
    const { data } = await apiClient.get<RecoveryOverzichtAntwoord>(
      "/api/admin/recovery-mails",
      { params: { dagen } },
    );
    return data;
  },
};

// Eigen checkout R1b (25-08): de trechter en de opt-in-registraties.
export const checkoutAdminApi = {
  async trechter(dagen = 30): Promise<CheckoutTrechterAntwoord> {
    const { data } = await apiClient.get<CheckoutTrechterAntwoord>(
      "/api/admin/checkout",
      { params: { dagen } },
    );
    return data;
  },

  async verloop(
    bucket: "dag" | "week" = "dag",
    dagen = 30,
  ): Promise<CheckoutVerloopAntwoord> {
    const { data } = await apiClient.get<CheckoutVerloopAntwoord>(
      "/api/admin/checkout/verloop",
      { params: { bucket, dagen } },
    );
    return data;
  },

  async assets(): Promise<CheckoutAssetOverzicht> {
    const { data } = await apiClient.get<CheckoutAssetOverzicht>(
      "/api/admin/checkout/assets",
    );
    return data;
  },

  async zetAssetReferentie(bestand: string): Promise<CheckoutAssetStand> {
    const { data } = await apiClient.post<CheckoutAssetStand>(
      `/api/admin/checkout/assets/${encodeURIComponent(bestand)}/referentie`,
    );
    return data;
  },

  async optins(): Promise<CheckoutOptinOverzichtAntwoord> {
    const { data } = await apiClient.get<CheckoutOptinOverzichtAntwoord>(
      "/api/admin/optins",
    );
    return data;
  },

  // Vangnetronde 20-09: de noodrem, de ankers en de ordercontrole.
  async laagstand(): Promise<CheckoutLaagstand> {
    const { data } = await apiClient.get<CheckoutLaagstand>(
      "/api/admin/checkout/laag",
    );
    return data;
  },

  async zetLaagstand(
    actief: boolean,
    onderdeel?: string,
  ): Promise<CheckoutLaagstand> {
    const { data } = await apiClient.put<CheckoutLaagstand>(
      "/api/admin/checkout/laag",
      { actief, onderdeel: onderdeel ?? null },
    );
    return data;
  },

  async ankers(): Promise<CheckoutAnkerOverzicht> {
    const { data } = await apiClient.get<CheckoutAnkerOverzicht>(
      "/api/admin/checkout/ankers",
    );
    return data;
  },

  async ordercontrole(): Promise<CheckoutOrderControle> {
    const { data } = await apiClient.get<CheckoutOrderControle>(
      "/api/admin/checkout/ordercontrole",
    );
    return data;
  },

  // Checkout 11: foutmeetpunten en het 1-uursignaal van de bewaking.
  async signalen(): Promise<CheckoutSignalen> {
    const { data } = await apiClient.get<CheckoutSignalen>(
      "/api/admin/checkout/signalen",
    );
    return data;
  },

  // Checkout 12: de meetlat (contract D), vier cijfers per periode.
  async meetlat(
    indeling: CheckoutMeetlatIndeling = "dag",
    perioden = 14,
  ): Promise<CheckoutMeetlat> {
    const { data } = await apiClient.get<CheckoutMeetlat>(
      "/api/admin/checkout/meetlat",
      { params: { indeling, perioden } },
    );
    return data;
  },

  async zetAssetSignaal(actief: boolean): Promise<{ actief: boolean }> {
    const { data } = await apiClient.put<{ actief: boolean }>(
      "/api/admin/checkout/assets/signaal",
      { actief },
    );
    return data;
  },
};

// ── Checkout 16a: het meetscherm Beheer > Checkout-meting ─────────────
// Contract: app/schemas/checkout_meting.py. Percentages in procenten (83.0),
// verschillen in punten, bedragen in centen, datums als "YYYY-MM-DD".

export type CkVergelijking = "vorige" | "4w" | "jaar" | "voor_laatste_plaatsing";
export type CkOordeel = "binnen_ruis" | "let_op";
export type CkWijzigingOordeel =
  | "beter"
  | "slechter"
  | "geen_verschil"
  | "nog_te_vroeg"
  // Alleen bij een incident: nooit beter of slechter.
  | "geen_dip"
  | "dip";
export type CkMarkeringSoort = "plaatsing" | "schakelaar" | "proef" | "incident" | "notitie";

export interface CkPeriode {
  van: string;
  tot: string;
  label: string;
  met_vandaag?: boolean;
}

export interface CkKpiVergelijking {
  soort: CkVergelijking;
  label: string;
  periode: CkPeriode;
  waarde: number | null;
  verschil: number | null;
  oordeel: CkOordeel | null;
}

export interface CkKpi {
  sleutel:
    | "betaald"
    | "start_naar_betaald"
    | "mobiel_naar_betaalpoging"
    | "afhakers_per_100"
    | "mislukt";
  titel: string;
  waarde: number | null;
  eenheid: "aantal" | "procent" | "per_100";
  teller?: number | null;
  noemer?: number | null;
  extra?: number | null;
  let_op?: boolean;
  hoger_is_beter?: boolean;
  vergelijkingen: CkKpiVergelijking[];
}

export interface CkProefKant {
  kant: "a" | "b";
  label: string;
  starts: number;
  betaald: number;
  percentage: number | null;
}

export interface CkProefUitslag {
  sleutel: string;
  naam: string;
  kanten: CkProefKant[];
  zekerheid: number | null;
  winnaar?: "a" | "b" | null;
  nodig_per_kant?: number | null;
}

export interface CkKpiAntwoord {
  periode: CkPeriode;
  kpis: CkKpi[];
  ruisgrens: number;
  proef?: CkProefUitslag | null;
}

export type CkTrechterSleutel =
  | "start"
  | "gegevens"
  | "mail"
  | "inloggen"
  | "adres"
  | "bezorging"
  | "overzicht"
  | "betaalpoging"
  | "betaald";

export interface CkTrechterRij {
  sleutel: CkTrechterSleutel;
  titel: string;
  sub?: boolean;
  sessies: number;
  weg?: number | null;
  verlies?: number | null;
  mobiel_weg?: number | null;
  desktop_weg?: number | null;
  percentage?: number | null;
  mobiel_percentage?: number | null;
  desktop_percentage?: number | null;
  let_op?: boolean;
}

export interface CkTeller {
  naam: string;
  aantal: number;
  sessies?: number | null;
}

export interface CkStapVerdieping {
  stap: "start" | "gegevens" | "bezorging" | "overzicht" | "betaalpoging";
  afhakers: number;
  mediaan_verblijf_afhakers_s?: number | null;
  mediaan_verblijf_kopers_s?: number | null;
  laatste_doel?: CkTeller[];
  formulierfout_sessies?: number;
  formulierfouten?: CkTeller[];
  leeg_adresformulier?: number | null;
  inloglink_gevraagd?: number | null;
  inloglink_besteld?: number | null;
  mand_gemiddeld_cents?: number | null;
  mand_mediaan_cents?: number | null;
}

export interface CkTrechterAntwoord {
  periode: CkPeriode;
  apparaat?: "alle" | "mobiel" | "desktop" | "tablet";
  starts: number;
  rijen: CkTrechterRij[];
  verdieping: CkStapVerdieping[];
  proef?: CkProefUitslag | null;
}

export interface CkMarkering {
  id: string;
  tijdstip: string;
  tot?: string | null;
  soort: CkMarkeringSoort;
  tekst: string;
  door: string;
}

export interface CkMarkeringVerzoek {
  tijdstip: string;
  tot?: string | null;
  soort: CkMarkeringSoort;
  tekst: string;
}

export interface CkWeekPunt {
  week_start: string;
  label: string;
  starts: number;
  betaald: number;
  percentage: number | null;
  mobiel_percentage: number | null;
  volledig?: boolean;
}

export interface CkWeekreeksAntwoord {
  weken: CkWeekPunt[];
  band_laag: number | null;
  band_hoog: number | null;
  markeringen: CkMarkering[];
}

export interface CkMethodeRij {
  methode: string;
  betaald: number;
  mislukt: number;
  /** Billink: alleen ter informatie, niet in mislukt of slaagt. */
  afgewezen?: number;
  /** betaald / (betaald + mislukt). */
  slaagt: number | null;
}

export interface CkMethodenAntwoord {
  periode: CkPeriode;
  methoden: CkMethodeRij[];
  /** Sessies met minstens een mislukte betaling, gelijk aan de tegel. */
  mislukt_totaal: number;
  betaald_totaal?: number;
  /** betaald_totaal / (betaald_totaal + mislukt_totaal), gelijk aan de tegel. */
  slaagt_totaal?: number | null;
  billink_afgewezen?: number;
  opnieuw_geprobeerd: number;
  betaald_zonder_bedankpagina: number | null;
  ordercontrole_gecontroleerd?: number | null;
  ordercontrole_afwijkingen?: number | null;
}

export interface CkWijzigingKant {
  van: string;
  tot: string;
  starts: number;
  conversie: number | null;
  mobiel: number | null;
  mislukt: number;
  /** betaald / (betaald + mislukt). */
  slaagt?: number | null;
}

export interface CkWijzigingRij {
  markering: CkMarkering;
  voor: CkWijzigingKant;
  na: CkWijzigingKant;
  oordeel: CkWijzigingOordeel;
  verschil: number | null;
  dagen_na: number;
}

export interface CkWijzigingenAntwoord {
  dagen: number;
  min_starts: number;
  rijen: CkWijzigingRij[];
}

export interface CkUurRij {
  uur: number;
  starts: number;
  betaalpogingen: number;
  percentage: number | null;
}

export interface CkUurAntwoord {
  periode: CkPeriode;
  uren: CkUurRij[];
}

export interface CkInloglinkAntwoord {
  periode: CkPeriode;
  gevraagd: number;
  via_link_ingelogd: number;
  besteld_48u: number;
  besteld_10min: number;
  kliks: number;
  kliks_binnen_2min: number;
}

export interface CkOverzichtAfhakersAntwoord {
  periode: CkPeriode;
  zonder_bestelklik: number;
  alsnog_betaalpoging: number;
  betaalde_verzending: number;
  betaalde_verzending_mand_gemiddeld_cents: number | null;
  gratis_verzending: number;
  gratis_verzending_mand_mediaan_cents: number | null;
}

export interface CkProef {
  sleutel: string;
  naam: string;
  kant_a: string;
  kant_b: string;
  /** Percentage sessies dat kant B krijgt (50 = half om half). */
  verdeling: number;
  actief: boolean;
  gestart_op: string | null;
  gestopt_op: string | null;
}

export interface CkProevenAntwoord {
  proeven: CkProef[];
}

/** De bestaande signalen plus de twee scriptfout-tellers van 16a. */
export interface CkSignalen extends CheckoutSignalen {
  scriptfouten_eigen?: number;
  scriptfouten_derden?: number;
}

export interface CkPeriodeVraag {
  van: string;
  tot: string;
  vergelijk: CkVergelijking;
}

async function ckGet<T>(
  pad: string,
  vraag?: CkPeriodeVraag,
  extra?: Record<string, string>,
): Promise<T> {
  const { data } = await apiClient.get<T>(`/api/admin/checkout/${pad}`, {
    params: vraag
      ? { van: vraag.van, tot: vraag.tot, vergelijk: vraag.vergelijk, ...extra }
      : extra,
  });
  return data;
}

export const checkoutMetingApi = {
  kpi: (v: CkPeriodeVraag) => ckGet<CkKpiAntwoord>("kpi", v),
  trechter: (v: CkPeriodeVraag) => ckGet<CkTrechterAntwoord>("trechter", v),
  weekreeks: (v: CkPeriodeVraag) => ckGet<CkWeekreeksAntwoord>("weekreeks", v),
  methoden: (v: CkPeriodeVraag) => ckGet<CkMethodenAntwoord>("methoden", v),
  wijzigingen: (v: CkPeriodeVraag) => ckGet<CkWijzigingenAntwoord>("wijzigingen", v),
  uur: (v: CkPeriodeVraag) => ckGet<CkUurAntwoord>("uur", v),
  inloglink: (v: CkPeriodeVraag) => ckGet<CkInloglinkAntwoord>("inloglink", v),
  overzichtAfhakers: (v: CkPeriodeVraag) =>
    ckGet<CkOverzichtAfhakersAntwoord>("overzicht-afhakers", v),
  signalen: () => ckGet<CkSignalen>("signalen"),
  proeven: () => ckGet<CkProevenAntwoord>("proeven"),
  /** Uitslag van één proef; de server rekent over de looptijd van de proef. */
  proefUitslag: async (sleutel: string, v: CkPeriodeVraag) =>
    (await ckGet<CkKpiAntwoord>("kpi", v, { proef: sleutel })).proef ?? null,

  async markeringToevoegen(verzoek: CkMarkeringVerzoek): Promise<unknown> {
    const { data } = await apiClient.post<unknown>(
      "/api/admin/checkout/markeringen",
      verzoek,
    );
    return data;
  },

  async proefStarten(sleutel: string, verdeling: number): Promise<CkProef> {
    const { data } = await apiClient.post<CkProef>(
      `/api/admin/checkout/proeven/${encodeURIComponent(sleutel)}/start`,
      { verdeling },
    );
    return data;
  },

  async proefStoppen(sleutel: string): Promise<CkProef> {
    const { data } = await apiClient.post<CkProef>(
      `/api/admin/checkout/proeven/${encodeURIComponent(sleutel)}/stop`,
    );
    return data;
  },
};

export const recoveryPubliekApi = {
  async afmelden(token: string): Promise<RecoveryAfmeldAntwoord> {
    const { data } = await apiClient.post<RecoveryAfmeldAntwoord>(
      "/api/public/recovery/afmelden",
      { token },
    );
    return data;
  },
};

export const voorraadmeldingenApi = {
  async overzicht(dagen = 30): Promise<VoorraadmeldingenAntwoord> {
    const { data } = await apiClient.get<VoorraadmeldingenAntwoord>(
      "/api/admin/voorraadmeldingen",
      { params: { dagen } },
    );
    return data;
  },
};

export const emailsApi = {
  async lijst(params: EmailLijstParams = {}): Promise<EmailLijstAntwoord> {
    const { data } = await apiClient.get<EmailLijstAntwoord>("/api/emails", {
      params,
    });
    return data;
  },

  /** Sidebar-tellers (Trengo-ronde 03-08): Onbeantwoord / Aan mij / Wachten. */
  async tellers(): Promise<EmailTellersAntwoord> {
    const { data } =
      await apiClient.get<EmailTellersAntwoord>("/api/emails/tellers");
    return data;
  },

  async detail(id: string): Promise<EmailDetailAntwoord> {
    const { data } = await apiClient.get<EmailDetailAntwoord>(
      `/api/emails/${id}`,
    );
    return data;
  },

  async thread(id: string): Promise<EmailThreadAntwoord> {
    const { data } = await apiClient.get<EmailThreadAntwoord>(
      `/api/emails/${id}/thread`,
    );
    return data;
  },

  /** Gemarkeerd-bewaren (AVG) op de conversatie van deze mail. */
  async zetBewaren(id: string, bewaren: boolean): Promise<EmailDetailAntwoord> {
    const { data } = await apiClient.post<EmailDetailAntwoord>(
      `/api/emails/${id}/bewaren`,
      { bewaren },
    );
    return data;
  },

  /** Bundeling (05-08): alle onbeantwoorde mails van dezelfde klant. */
  async bundel(id: string): Promise<EmailBundelAntwoord> {
    const { data } = await apiClient.get<EmailBundelAntwoord>(
      `/api/emails/${id}/bundel`,
    );
    return data;
  },

  /** Bijlage-metadata bij één bericht (mail-leeskolom 02-08). */
  async bijlagen(id: string): Promise<EmailBijlagenAntwoord> {
    const { data } = await apiClient.get<EmailBijlagenAntwoord>(
      `/api/emails/${id}/bijlagen`,
    );
    return data;
  },

  /** De bytes van één bijlage, via de bestaande auth (Bearer) — als
   *  blob zodat de UI er een object-URL van maakt (zelfde patroon als
   *  ``adminChatApi.bijlage``). */
  async bijlageInhoud(id: string, bijlageId: string): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      `/api/emails/${id}/bijlagen/${bijlageId}/inhoud`,
      { responseType: "blob" },
    );
    return data;
  },

  async verstuurAntwoordMetBijlagen(
    id: string,
    invoer: {
      tekst_html: string;
      send_intent_id: string;
      bijlagen: File[];
      uit_spar_chat?: boolean;
      stoplicht_correctie?: string;
      onderwerp?: string;
      naar_email?: string;
      bevestig_noreply?: boolean;
      cc?: string[];
      bcc?: string[];
      mailbox_id?: string;
    },
  ): Promise<EmailAntwoord> {
    const fd = new FormData();
    fd.append("tekst_html", invoer.tekst_html);
    fd.append("send_intent_id", invoer.send_intent_id);
    if (invoer.uit_spar_chat) fd.append("uit_spar_chat", "true");
    if (invoer.stoplicht_correctie)
      fd.append("stoplicht_correctie", invoer.stoplicht_correctie);
    if (invoer.onderwerp) fd.append("onderwerp", invoer.onderwerp);
    if (invoer.naar_email) fd.append("naar_email", invoer.naar_email);
    if (invoer.bevestig_noreply) fd.append("bevestig_noreply", "true");
    // CC/BCC gaan als één komma-gescheiden veld mee; de server splitst
    // en valideert (multipart kent geen lijsten van strings).
    if (invoer.cc?.length) fd.append("cc", invoer.cc.join(","));
    if (invoer.bcc?.length) fd.append("bcc", invoer.bcc.join(","));
    if (invoer.mailbox_id) fd.append("mailbox_id", invoer.mailbox_id);
    for (const f of invoer.bijlagen) fd.append("bijlagen", f, f.name);
    const { data } = await apiClient.post<EmailAntwoord>(
      `/api/emails/${id}/antwoord-met-bijlagen`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
  },

  /** Zelf een mail starten — los van een bestaande thread. */
  async verstuurNieuweMail(invoer: {
    naar_email: string;
    onderwerp: string;
    tekst_html: string;
    send_intent_id: string;
    klant_id?: string;
    mailbox_id?: string;
    bevestig_noreply?: boolean;
    cc?: string[];
    bcc?: string[];
  }): Promise<EmailDetailAntwoord> {
    const { data } = await apiClient.post<EmailDetailAntwoord>(
      "/api/emails/nieuw",
      invoer,
    );
    return data;
  },

  /** Nieuwe mail mét bijlagen (JP 03-08) — zelfde uploadpad en dezelfde
   *  limieten als een antwoord met bijlagen. */
  async verstuurNieuweMailMetBijlagen(invoer: {
    naar_email: string;
    onderwerp: string;
    tekst_html: string;
    send_intent_id: string;
    bijlagen: File[];
    klant_id?: string;
    mailbox_id?: string;
    bevestig_noreply?: boolean;
    cc?: string[];
    bcc?: string[];
  }): Promise<EmailDetailAntwoord> {
    const fd = new FormData();
    fd.append("naar_email", invoer.naar_email);
    fd.append("onderwerp", invoer.onderwerp);
    fd.append("tekst_html", invoer.tekst_html);
    fd.append("send_intent_id", invoer.send_intent_id);
    if (invoer.klant_id) fd.append("klant_id", invoer.klant_id);
    if (invoer.mailbox_id) fd.append("mailbox_id", invoer.mailbox_id);
    if (invoer.bevestig_noreply) fd.append("bevestig_noreply", "true");
    if (invoer.cc?.length) fd.append("cc", invoer.cc.join(","));
    if (invoer.bcc?.length) fd.append("bcc", invoer.bcc.join(","));
    for (const f of invoer.bijlagen) fd.append("bijlagen", f, f.name);
    const { data } = await apiClient.post<EmailDetailAntwoord>(
      "/api/emails/nieuw-met-bijlagen",
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
  },

  async verstuurAntwoord(
    id: string,
    invoer: {
      tekst_html: string;
      send_intent_id: string;
      uit_spar_chat?: boolean;
      stoplicht_correctie?: string;
      onderwerp?: string;
      naar_email?: string;
      bevestig_noreply?: boolean;
      cc?: string[];
      bcc?: string[];
      mailbox_id?: string;
    },
  ): Promise<EmailAntwoord> {
    const { data } = await apiClient.post<EmailAntwoord>(
      `/api/emails/${id}/antwoord`,
      invoer,
    );
    return data;
  },

  /** De blij-knop (Sanne leert 2): het concept ongewijzigd de deur uit.
   *
   *  Er gaat bewust GEEN tekst mee: de server pakt `claude_suggestie`
   *  zelf. Zou de tekst hier vandaan komen, dan kon een stilletjes
   *  bijgewerkt concept meetellen als goedkeuring en promoveert een
   *  vraagtype op een teller die niets meet. Wie iets wil wijzigen,
   *  gebruikt verstuurAntwoord — dat is dan een correctie. */
  async keurConceptGoed(
    id: string,
    sendIntentId: string,
  ): Promise<EmailAntwoord> {
    const { data } = await apiClient.post<EmailAntwoord>(
      `/api/emails/${id}/goedkeuren`,
      { send_intent_id: sendIntentId },
    );
    return data;
  },

  /** "Dit had niet zo de deur uit gemogen": zet het vraagtype terug.
   *  Werkt alleen op een mail die Sanne zelf verstuurde. */
  async markeerAntwoordFout(id: string): Promise<VraagtypeStandKort> {
    const { data } = await apiClient.post<VraagtypeStandKort>(
      `/api/emails/${id}/markeer-fout`,
    );
    return data;
  },

  /** Spar v2: de gepersisteerde sparsessie voor deze mail. De beurten
   *  zelf lopen streamend via useSparStream (SSE, geen axios). */
  async sparGeschiedenis(id: string): Promise<SparGeschiedenisAntwoord> {
    const { data } = await apiClient.get<SparGeschiedenisAntwoord>(
      `/api/emails/${id}/spar-chat`,
    );
    return data;
  },

  /** Schrijfhulp van Sanne (JP 05-08): ruwe invoer → nette mailtekst,
   *  of de huidige editorinhoud bijgestuurd. Verstuurt nooit zelf. */
  async schrijfhulp(invoer: {
    modus: SchrijfhulpModus;
    invoer?: string;
    huidige_tekst?: string;
    aanwijzing?: string;
    klant_id?: string;
    onderwerp?: string;
  }): Promise<SchrijfhulpAntwoord> {
    const { data } = await apiClient.post<SchrijfhulpAntwoord>(
      "/api/emails/schrijfhulp",
      invoer,
    );
    return data;
  },

  async genereerSuggestie(
    id: string,
    opties?: { forceer_opnieuw?: boolean; richtlijn?: string | null },
  ): Promise<EmailDetailAntwoord> {
    const { data } = await apiClient.post<EmailDetailAntwoord>(
      `/api/emails/${id}/suggestie`,
      opties ?? {},
    );
    return data;
  },

  async wijzigPersonaToewijzing(
    id: string,
    medewerker_id: string | null,
  ): Promise<EmailAntwoord> {
    const { data } = await apiClient.patch<EmailAntwoord>(
      `/api/emails/${id}/toewijzing`,
      { medewerker_id },
    );
    return data;
  },

  async wijzigStatus(
    id: string,
    status: EmailStatus,
    wekker_tot?: string,
  ): Promise<EmailAntwoord> {
    const { data } = await apiClient.patch<EmailAntwoord>(
      `/api/emails/${id}/status`,
      wekker_tot ? { status, wekker_tot } : { status },
    );
    return data;
  },

  async assign(
    id: string,
    gebruiker_id: string | null,
  ): Promise<EmailAntwoord> {
    const { data } = await apiClient.patch<EmailAntwoord>(
      `/api/emails/${id}/assign`,
      { gebruiker_id },
    );
    return data;
  },

  /** Aan Sanne toewijzen / losmaken (fixronde 04-08). Werkverdeling:
   *  de mail verhuist naar "Bij Sanne" en het concept wordt ververst;
   *  verzenden blijft altijd bij JP. */
  async bijSanne(id: string, aan: boolean): Promise<EmailAntwoord> {
    const { data } = await apiClient.patch<EmailAntwoord>(
      `/api/emails/${id}/bij-sanne`,
      { aan },
    );
    return data;
  },

  async syncNu(): Promise<{ gestart_op: string }> {
    const { data } = await apiClient.post<{ gestart_op: string }>(
      "/api/emails/sync-nu",
    );
    return data;
  },

  async snooze(id: string, tot: Date): Promise<EmailAntwoord> {
    const { data } = await apiClient.post<EmailAntwoord>(
      `/api/emails/${id}/snooze`,
      { tot: tot.toISOString() },
    );
    return data;
  },

  async unsnooze(id: string): Promise<EmailAntwoord> {
    const { data } = await apiClient.delete<EmailAntwoord>(
      `/api/emails/${id}/snooze`,
    );
    return data;
  },

  async draftOpslaan(id: string, content: string): Promise<void> {
    // Backend-route is PATCH met veld "inhoud"; de oude PUT met
    // "content" gaf een stille 405, waardoor drafts nooit bewaard
    // werden (dagdeel-ronde 21-08).
    await apiClient.patch(`/api/emails/${id}/draft`, { inhoud: content });
  },

  /** Haal de voorbereide retour-actie bij dit concept weg (besluit JP
   *  15-09). Geen actie meer = een gewone mail. */
  async verwijderVoorbereideRetour(
    id: string,
  ): Promise<EmailDetailAntwoord> {
    const { data } = await apiClient.delete<EmailDetailAntwoord>(
      `/api/emails/${id}/voorbereide-retour`,
    );
    return data;
  },

  async koppelingVoorstellen(id: string): Promise<KoppelingVoorstel[]> {
    const { data } = await apiClient.get<KoppelingVoorstel[]>(
      `/api/emails/${id}/koppeling-voorstellen`,
    );
    return data;
  },

  async koppelKlant(
    email_id: string,
    klant_id: string,
  ): Promise<EmailAntwoord> {
    const { data } = await apiClient.post<EmailAntwoord>(
      `/api/emails/${email_id}/koppel-klant/${klant_id}`,
    );
    return data;
  },

  async negeerKoppeling(email_id: string, voorstel_id: string): Promise<void> {
    await apiClient.post(
      `/api/emails/${email_id}/negeer-koppeling/${voorstel_id}`,
    );
  },

  /** Maak een (foute of automatische) klantkoppeling ongedaan (03-08). */
  async ontkoppelKlant(email_id: string): Promise<EmailAntwoord> {
    const { data } = await apiClient.post<EmailAntwoord>(
      `/api/emails/${email_id}/ontkoppel-klant`,
    );
    return data;
  },

  async bulkActie(
    email_ids: string[],
    actie: "sluiten" | "spam" | "heropen" | "verwijderen",
  ): Promise<{ actie: string; bijgewerkt: number; overgeslagen: number }> {
    const { data } = await apiClient.post<{
      actie: string;
      bijgewerkt: number;
      overgeslagen: number;
    }>("/api/emails/bulk", { email_ids, actie });
    return data;
  },
};

// ────── Automatisering ──────

export const automatiseringApi = {
  /** Wat draait er werkelijk autonoom, met de stand van deze omgeving. */
  async overzicht(): Promise<AutomatiseringOverzichtItem[]> {
    const { data } = await apiClient.get<AutomatiseringOverzichtItem[]>(
      "/api/automatisering/overzicht",
    );
    return data;
  },

  async configs(): Promise<AutomatiseringConfig[]> {
    const { data } = await apiClient.get<AutomatiseringConfig[]>(
      "/api/automatisering/configs",
    );
    return data;
  },

  async update(
    actie_type: AutomatiseringActie,
    niveau: 0 | 2,
    dagelijks_max_autonoom: number,
  ): Promise<AutomatiseringConfig> {
    const { data } = await apiClient.put<AutomatiseringConfig>(
      `/api/automatisering/configs/${actie_type}`,
      { niveau, dagelijks_max_autonoom },
    );
    return data;
  },

  /** Storingsbewaking: actuele signaalstand + drempels + storingen. */
  async bewaking(): Promise<BewakingAntwoord> {
    const { data } = await apiClient.get<BewakingAntwoord>(
      "/api/automatisering/bewaking",
    );
    return data;
  },

  async bewakingWijzig(
    instellingen: BewakingInstellingen,
  ): Promise<BewakingInstellingen> {
    const { data } = await apiClient.put<BewakingInstellingen>(
      "/api/automatisering/bewaking",
      instellingen,
    );
    return data;
  },

  /** AVG-opschoning: instellingen + de laatste runs. */
  async opschoning(): Promise<OpschoningAntwoord> {
    const { data } = await apiClient.get<OpschoningAntwoord>(
      "/api/automatisering/opschoning",
    );
    return data;
  },

  async opschoningWijzig(
    instellingen: OpschoningInstellingen,
  ): Promise<OpschoningInstellingen> {
    const { data } = await apiClient.put<OpschoningInstellingen>(
      "/api/automatisering/opschoning",
      instellingen,
    );
    return data;
  },
};

// ────── Commitments ──────

export const mailboxenApi = {
  async lijst(): Promise<MailboxAntwoord[]> {
    const { data } = await apiClient.get<MailboxAntwoord[]>("/api/mailboxen");
    return data;
  },
};

export const commitmentsApi = {
  async lijst(params: { mijn?: boolean } = {}): Promise<Commitment[]> {
    const { data } = await apiClient.get<Commitment[]>("/api/commitments", {
      params,
    });
    return data;
  },

  async wijzig(id: string, status: CommitmentStatus): Promise<Commitment> {
    const { data } = await apiClient.patch<Commitment>(
      `/api/commitments/${id}`,
      { status },
    );
    return data;
  },

  async verwijder(id: string): Promise<void> {
    await apiClient.delete(`/api/commitments/${id}`);
  },
};

// Pushmeldingen (ronde E). Opvragen/afmelden gaat via POST met het
// endpoint in de body: push-endpoints zijn lange capability-URLs die
// niet in querystrings of accesslogs horen.
export const pushApi = {
  async config(): Promise<PushConfigAntwoord> {
    const { data } = await apiClient.get<PushConfigAntwoord>("/api/push/config");
    return data;
  },

  async abonneer(
    invoer: PushAbonnementInvoer,
  ): Promise<PushAbonnementAntwoord> {
    const { data } = await apiClient.post<PushAbonnementAntwoord>(
      "/api/push/abonnementen",
      invoer,
    );
    return data;
  },

  /** Stand van dit apparaat; null als het (nog) niet geabonneerd is. */
  async opvragen(endpoint: string): Promise<PushAbonnementAntwoord | null> {
    try {
      const { data } = await apiClient.post<PushAbonnementAntwoord>(
        "/api/push/abonnementen/opvragen",
        { endpoint },
      );
      return data;
    } catch (fout) {
      if (axios.isAxiosError(fout) && fout.response?.status === 404) {
        return null;
      }
      throw fout;
    }
  },

  async afmelden(endpoint: string): Promise<void> {
    await apiClient.post("/api/push/abonnementen/afmelden", { endpoint });
  },

  /**
   * Testmelding; zonder id naar alle apparaten van de gebruiker, met
   * id naar precies dat ene apparaat (sessie 4).
   */
  async testmelding(abonnementId?: string): Promise<PushTestAntwoord> {
    const { data } = await apiClient.post<PushTestAntwoord>(
      "/api/push/testmelding",
      { abonnement_id: abonnementId ?? null },
    );
    return data;
  },

  /**
   * Alle apparaten van de gebruiker met hun stand. POST omdat de
   * browser haar eigen endpoint meestuurt om zichzelf te herkennen —
   * dat hoort niet in een querystring.
   */
  async apparaten(endpoint?: string | null): Promise<PushApparaat[]> {
    const { data } = await apiClient.post<PushApparatenAntwoord>(
      "/api/push/apparaten",
      endpoint ? { endpoint } : null,
    );
    return data.apparaten;
  },

  /** Ruim de als verlopen gemarkeerde abonnementen op. */
  async opruimen(): Promise<PushOpruimAntwoord> {
    const { data } = await apiClient.post<PushOpruimAntwoord>(
      "/api/push/apparaten/opruimen",
    );
    return data;
  },
};

export const adminMedewerkersApi = {
  async lijst(actief?: boolean): Promise<MedewerkerAntwoord[]> {
    const { data } = await apiClient.get<MedewerkerAntwoord[]>(
      "/api/admin/medewerkers",
      { params: actief === undefined ? {} : { actief } },
    );
    return data;
  },

  async detail(id: string): Promise<MedewerkerAntwoord> {
    const { data } = await apiClient.get<MedewerkerAntwoord>(
      `/api/admin/medewerkers/${id}`,
    );
    return data;
  },

  async aanmaken(invoer: MedewerkerAanmaken): Promise<MedewerkerAntwoord> {
    const { data } = await apiClient.post<MedewerkerAntwoord>(
      "/api/admin/medewerkers",
      invoer,
    );
    return data;
  },

  async wijzigen(
    id: string,
    invoer: MedewerkerWijzigen,
  ): Promise<MedewerkerAntwoord> {
    const { data } = await apiClient.patch<MedewerkerAntwoord>(
      `/api/admin/medewerkers/${id}`,
      invoer,
    );
    return data;
  },

  async deactiveer(id: string): Promise<MedewerkerAntwoord> {
    const { data } = await apiClient.post<MedewerkerAntwoord>(
      `/api/admin/medewerkers/${id}/deactiveer`,
    );
    return data;
  },

  async uploadAvatar(id: string, bestand: File): Promise<MedewerkerAntwoord> {
    const fd = new FormData();
    fd.append("bestand", bestand);
    const { data } = await apiClient.post<MedewerkerAntwoord>(
      `/api/admin/medewerkers/${id}/avatar`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
  },

  async preview(id: string): Promise<MedewerkerPreviewAntwoord> {
    const { data } = await apiClient.get<MedewerkerPreviewAntwoord>(
      `/api/admin/medewerkers/${id}/preview`,
    );
    return data;
  },
};

export const adminKnowledgeApi = {
  async lijst(filters?: {
    zoek?: string;
    categorie?: string;
    actief?: boolean;
  }): Promise<KennisItemAntwoord[]> {
    const params: Record<string, string | boolean> = {};
    if (filters?.zoek) params.zoek = filters.zoek;
    if (filters?.categorie) params.categorie = filters.categorie;
    if (filters?.actief !== undefined) params.actief = filters.actief;
    const { data } = await apiClient.get<KennisItemAntwoord[]>(
      "/api/admin/knowledge",
      { params },
    );
    return data;
  },

  async categorieen(): Promise<string[]> {
    const { data } = await apiClient.get<string[]>(
      "/api/admin/knowledge/categorieen",
    );
    return data;
  },

  async detail(id: string): Promise<KennisItemAntwoord> {
    const { data } = await apiClient.get<KennisItemAntwoord>(
      `/api/admin/knowledge/${id}`,
    );
    return data;
  },

  async aanmaken(
    invoer: KennisItemAanmaken,
  ): Promise<KennisItemSchrijfAntwoord> {
    const { data } = await apiClient.post<KennisItemSchrijfAntwoord>(
      "/api/admin/knowledge",
      invoer,
    );
    return data;
  },

  async wijzigen(
    id: string,
    invoer: KennisItemWijzigen,
  ): Promise<KennisItemSchrijfAntwoord> {
    const { data } = await apiClient.patch<KennisItemSchrijfAntwoord>(
      `/api/admin/knowledge/${id}`,
      invoer,
    );
    return data;
  },

  async deactiveer(id: string): Promise<KennisItemAntwoord> {
    const { data } = await apiClient.post<KennisItemAntwoord>(
      `/api/admin/knowledge/${id}/deactiveer`,
    );
    return data;
  },

  async verwijder(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/knowledge/${id}`);
  },

  async opstelhulp(ruwe_tekst: string): Promise<KennisOpstelhulpVoorstel> {
    const { data } = await apiClient.post<KennisOpstelhulpVoorstel>(
      "/api/admin/knowledge/opstelhulp",
      { ruwe_tekst },
    );
    return data;
  },
};

/** Kennisitem-voorstel na een beantwoorde mail (ronde F). Admin-only:
 *  niet-admins krijgen een 403 en het venster verschijnt dan simpelweg
 *  niet (de check is een stil zijspoor van het verzenden). */
export const kennisVoorstelApi = {
  async check(emailId: string): Promise<KennisVoorstelCheck> {
    const { data } = await apiClient.get<KennisVoorstelCheck>(
      `/api/emails/${emailId}/kennis-voorstel`,
    );
    return data;
  },

  async opstellen(emailId: string): Promise<KennisOpstelhulpVoorstel> {
    const { data } = await apiClient.post<KennisOpstelhulpVoorstel>(
      `/api/emails/${emailId}/kennis-voorstel/opstellen`,
    );
    return data;
  },

  async accepteer(
    emailId: string,
    invoer: KennisVoorstelAccepteren,
  ): Promise<KennisVoorstelResultaat> {
    const { data } = await apiClient.post<KennisVoorstelResultaat>(
      `/api/emails/${emailId}/kennis-voorstel`,
      invoer,
    );
    return data;
  },
};

export const signatureSettingsApi = {
  async haal(): Promise<SignatureSettingsAntwoord> {
    const { data } = await apiClient.get<SignatureSettingsAntwoord>(
      "/api/admin/signature-settings",
    );
    return data;
  },

  async wijzig(
    invoer: SignatureSettingsWijzigen,
  ): Promise<SignatureSettingsAntwoord> {
    const { data } = await apiClient.patch<SignatureSettingsAntwoord>(
      "/api/admin/signature-settings",
      invoer,
    );
    return data;
  },
};

export const aiModelApi = {
  async haal(): Promise<AiModelAntwoord> {
    const { data } = await apiClient.get<AiModelAntwoord>(
      "/api/admin/ai-model",
    );
    return data;
  },

  async wijzig(invoer: AiModelWijzigen): Promise<AiModelAntwoord> {
    const { data } = await apiClient.put<AiModelAntwoord>(
      "/api/admin/ai-model",
      invoer,
    );
    return data;
  },
};

export const kostenApi = {
  async overzicht(periode: KostenPeriode): Promise<KostenOverzichtAntwoord> {
    const { data } = await apiClient.get<KostenOverzichtAntwoord>(
      "/api/admin/kosten",
      { params: { periode } },
    );
    return data;
  },

  // Doorklik van de kanalentabel (napleister 19-08):
  // categorie → dag → gesprek/mail → losse AI-call.
  async kanaalDagen(
    categorie: string,
    periode: KostenPeriode,
  ): Promise<KanaalDagenAntwoord> {
    const { data } = await apiClient.get<KanaalDagenAntwoord>(
      `/api/admin/kosten/kanaal/${categorie}/dagen`,
      { params: { periode } },
    );
    return data;
  },

  async kanaalItems(
    categorie: string,
    datum: string,
  ): Promise<KanaalItemsAntwoord> {
    const { data } = await apiClient.get<KanaalItemsAntwoord>(
      `/api/admin/kosten/kanaal/${categorie}/items`,
      { params: { datum } },
    );
    return data;
  },

  async kanaalCalls(
    categorie: string,
    datum: string,
    gekoppeldId: string | null,
  ): Promise<KanaalCallsAntwoord> {
    const { data } = await apiClient.get<KanaalCallsAntwoord>(
      `/api/admin/kosten/kanaal/${categorie}/calls`,
      {
        params: {
          datum,
          ...(gekoppeldId ? { gekoppeld_id: gekoppeldId } : {}),
        },
      },
    );
    return data;
  },
};

export const shopExtrasApi = {
  async overzicht(): Promise<ShopExtrasOverzichtAntwoord> {
    const { data } = await apiClient.get<ShopExtrasOverzichtAntwoord>(
      "/api/admin/shop-extras",
    );
    return data;
  },

  async wijzig(
    sleutel: string,
    invoer: ShopExtraWijzigen,
  ): Promise<ShopExtrasOverzichtAntwoord> {
    const { data } = await apiClient.put<ShopExtrasOverzichtAntwoord>(
      `/api/admin/shop-extras/${sleutel}`,
      invoer,
    );
    return data;
  },

  /** Alleen de aan/uit-schakelaar (fixronde 08-09): die schrijft direct
   *  weg, los van de Opslaan-knop voor de velden. */
  async zetActief(
    sleutel: string,
    actief: boolean,
  ): Promise<ShopExtrasOverzichtAntwoord> {
    const { data } = await apiClient.patch<ShopExtrasOverzichtAntwoord>(
      `/api/admin/shop-extras/${sleutel}/actief`,
      { actief },
    );
    return data;
  },

  // Beheerblok "Laatste aanvragen" bij de herbestelflow (spoedronde
  // 16-08): per aanvraag alleen tijdstip en uitkomstcode, geen adres.
  async herbestelAanvragen(): Promise<HerbestelAanvraagLogAntwoord> {
    const { data } = await apiClient.get<HerbestelAanvraagLogAntwoord>(
      "/api/admin/herbestel/aanvragen",
    );
    return data;
  },

  // Beheerblok "Betalingen" (herbestel-afrekenen 17-08): de hele
  // keten per rij, alarmen bovenaan, zonder adres of naam.
  async herbestelBetalingen(): Promise<HerbestelBetalingenAntwoord> {
    const { data } = await apiClient.get<HerbestelBetalingenAntwoord>(
      "/api/admin/herbestel/betalingen",
    );
    return data;
  },
  // Beheerhendel: orderaanmaak van één betaling blokkeren/vrijgeven.
  async herbestelBetalingBlokkade(
    referentie: string,
    blokkade: boolean,
  ): Promise<void> {
    await apiClient.post("/api/admin/herbestel/betalingen/blokkade", {
      referentie,
      blokkade,
    });
  },
  // Hotfix 14-09: JP maakte de order zelf; rij afsluiten als
  // "handmatig afgerond, order X" zodat de wachtrij stopt.
  async herbestelBetalingHandmatig(
    referentie: string,
    ordernummer: string,
  ): Promise<HerbestelOrderStandAntwoord> {
    const { data } = await apiClient.post<HerbestelOrderStandAntwoord>(
      "/api/admin/herbestel/betalingen/handmatig",
      { referentie, ordernummer },
    );
    return data;
  },
  // Testlink-ronde 15-09: meteen een geldige bestellink om het
  // herbestelscherm zelf te bekijken. Geen mail, één uur geldig, en
  // alles wat erachter gebeurt telt apart in de trechter.
  async herbestelTestlink(
    email: string,
  ): Promise<HerbestelTestlinkAntwoord> {
    const { data } = await apiClient.post<HerbestelTestlinkAntwoord>(
      "/api/admin/herbestel/testlink",
      { email },
    );
    return data;
  },
  // Knop "herbouw opnieuw": blokkade eraf, volgende poging nú.
  async herbestelBetalingOpnieuw(
    referentie: string,
  ): Promise<HerbestelOrderStandAntwoord> {
    const { data } = await apiClient.post<HerbestelOrderStandAntwoord>(
      "/api/admin/herbestel/betalingen/opnieuw",
      { referentie },
    );
    return data;
  },
};

export interface KennisGatItem {
  id: string;
  email_id: string | null;
  zoekterm: string | null;
  categorie: string | null;
  beste_afstand: number | null;
  aantal_kandidaten: number;
  aangemaakt_op: string;
  /** Ronde F: gekoppeld [CONCEPT]-item uit het kennisitem-voorstel. */
  concept_item_id: string | null;
}

export interface KennisGatenAntwoord {
  open_aantal: number;
  items: KennisGatItem[];
}

export const adminKennisGatenApi = {
  async lijst(): Promise<KennisGatenAntwoord> {
    const { data } = await apiClient.get<KennisGatenAntwoord>(
      "/api/admin/kennis-gaten",
    );
    return data;
  },

  async oplossen(id: string): Promise<KennisGatItem> {
    const { data } = await apiClient.post<KennisGatItem>(
      `/api/admin/kennis-gaten/${id}/oplossen`,
    );
    return data;
  },
};

export interface ProductGatItem {
  lightspeed_product_id: string;
  naam: string;
  product_slug: string | null;
  categorie: string;
  diersoort: string;
  ontbrekende_velden: string[];
}

export interface ProductGatenAntwoord {
  totaal_producten: number;
  aantal_met_gaten: number;
  aantal_compleet: number;
  aantal_uitgesloten: number;
  aantal_energie_geschat: number;
  zonder_slug: number;
  per_veld_ontbrekend: Record<string, number>;
  veld_labels: Record<string, string>;
  items: ProductGatItem[];
}

export const adminProductGatenApi = {
  async rapport(): Promise<ProductGatenAntwoord> {
    const { data } = await apiClient.get<ProductGatenAntwoord>(
      "/api/admin/product-gaten",
    );
    return data;
  },

  async downloadCsv(): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      "/api/admin/product-gaten/export.csv",
      { responseType: "blob" },
    );
    return data;
  },
};

export interface AllergenenControleItem {
  lightspeed_product_id: string;
  naam: string;
  categorie: string;
  diersoort: string;
  samenstelling: string | null;
  tags: Record<string, string>;
  bron: string | null;
  graanvrij_claim: boolean | null;
  aantal_onzeker: number;
}

export interface AllergenenControlelijstAntwoord {
  totaal: number;
  items: AllergenenControleItem[];
}

export const adminAllergenenControleApi = {
  async lijst(): Promise<AllergenenControlelijstAntwoord> {
    const { data } = await apiClient.get<AllergenenControlelijstAntwoord>(
      "/api/admin/allergenen-controle",
    );
    return data;
  },

  async beoordeel(
    lightspeedProductId: string,
    tagsCorrectie: Record<string, string> | null,
  ): Promise<AllergenenControleItem> {
    const { data } = await apiClient.post<AllergenenControleItem>(
      `/api/admin/allergenen-controle/${lightspeedProductId}`,
      { tags_correctie: tagsCorrectie },
    );
    return data;
  },
};

export interface BronItem {
  id: string;
  titel: string;
  soort: string;
  vindplaats: string | null;
  versie: string | null;
  publicatiedatum: string | null;
  onderwerpen: string | null;
  houdbaarheidsdatum: string | null;
  actief: boolean;
  aantal_chunks: number;
  aantal_geembed: number;
  /** "geen" | "geldig" | "binnenkort" | "verlopen" */
  status: string;
}

export interface BronnenAntwoord {
  totaal: number;
  verlopen: number;
  binnenkort: number;
  zonder_embedding: number;
  binnenkort_dagen: number;
  items: BronItem[];
}

export const adminBronnenApi = {
  async overzicht(): Promise<BronnenAntwoord> {
    const { data } = await apiClient.get<BronnenAntwoord>("/api/admin/bronnen");
    return data;
  },
};

export interface EvalKanaalCijfers {
  cases: number;
  geslaagd: number;
  gemiddelde_score: number;
}

export interface EvalRunItem {
  id: string;
  gestart_op: string;
  voltooid_op: string | null;
  evalset_versie: string | null;
  aantal_cases: number;
  aantal_geslaagd: number;
  gemiddelde_score: string;
  /** Cijfers per kanaal (mail/chat); null bij oudere of mislukte runs. */
  cijfers_per_kanaal: Record<string, EvalKanaalCijfers> | null;
  maand_kosten_eur: string;
  /** "voltooid" | "overgeslagen_plafond" | "mislukt" */
  status: string;
  reden: string | null;
}

export interface KwaliteitsrunAntwoord {
  ingeschakeld: boolean;
  maandplafond_eur: number;
  laatste_rapport_markdown: string | null;
  runs: EvalRunItem[];
}

export const adminKwaliteitsrunApi = {
  async overzicht(): Promise<KwaliteitsrunAntwoord> {
    const { data } = await apiClient.get<KwaliteitsrunAntwoord>(
      "/api/admin/kwaliteitsrun",
    );
    return data;
  },
};

// ────── Leerrondes (Sanne-leerlus stap 1, 19-08) ──────

export interface LeerVoorstelItem {
  id: string;
  volgnummer: number;
  /** "regel" | "kennisitem" */
  soort: string;
  titel: string;
  voorstel: string;
  onderbouwing: string | null;
  /** Stap 1: altijd "wacht_op_jp" — de goedkeurflow is stap 2. */
  status: string;
  kennis_item_id: string | null;
}

export interface LeerRondeItem {
  id: string;
  gestart_op: string;
  voltooid_op: string | null;
  periode_start: string;
  periode_eind: string;
  aantal_verzendingen: number;
  aantal_gewijzigd: number;
  aantal_spar_correcties: number;
  aantal_voorstellen: number;
  maand_kosten_eur: string;
  /** "voltooid" | "overgeslagen_plafond" | "geen_data" | "mislukt" */
  status: string;
  analyse_samenvatting: string | null;
  reden: string | null;
  voorstellen: LeerVoorstelItem[];
}

export interface LeerrondesAntwoord {
  ingeschakeld: boolean;
  rondes: LeerRondeItem[];
}

export const adminLeerrondesApi = {
  async overzicht(): Promise<LeerrondesAntwoord> {
    const { data } = await apiClient.get<LeerrondesAntwoord>(
      "/api/admin/leerrondes",
    );
    return data;
  },
};

// ── Stoplicht per vraagtype (Sanne leert 2) ─────────────────────────

/** Stoplicht sinds sessie 50: oranje, akkoord of groen. */
export type VraagtypeStandKleur = "oranje" | "akkoord" | "groen";

/** De stand van één vraagtype, zoals het mailscherm hem toont. */
export interface VraagtypeStandKort {
  vraagtype: string;
  stand: VraagtypeStandKleur;
  reeks: number;
}

// ── Vrijdagtoets (Sanne leert 2) ────────────────────────────────────

export interface SanneToetsMail {
  nr: number;
  categorie: string;
  gedraaid: boolean;
  overtredingen: string[];
  terecht: string[];
  afsluitwens: boolean;
  tools: string[];
  feitenkern: Record<string, unknown>;
  tekens: number;
  fout: string | null;
  oordeel: "beter" | "gelijk" | "slechter";
}

export interface SanneToetsReeks {
  nr: number;
  runs: number;
  stabiel: boolean;
  verschillen: string[];
}

export interface SanneToetsRunItem {
  id: string;
  gestart_op: string;
  voltooid_op: string | null;
  status: string;
  bron: string;
  variant: string;
  aantal_mails: number;
  aantal_gedraaid: number;
  aantal_schoon: number;
  aantal_overtredingen: number;
  beter: number;
  gelijk: number;
  slechter: number;
  reeks_mails: number;
  reeks_stabiel: number;
  korte_uitslag: string;
  duur_seconden: number | null;
  mails: SanneToetsMail[];
  reeks: SanneToetsReeks[];
  reden: string | null;
}

export interface SanneToetsOverzicht {
  runs: SanneToetsRunItem[];
  drempel_slechter: number;
  bezig: boolean;
  bezig_sinds: string | null;
  droogstand: boolean;
}

export interface SanneToetsStart {
  gestart: boolean;
  stand: string;
}

export const adminSanneToetsApi = {
  async overzicht(): Promise<SanneToetsOverzicht> {
    const { data } = await apiClient.get<SanneToetsOverzicht>(
      "/api/admin/sanne-toets",
    );
    return data;
  },
  async nuDraaien(): Promise<SanneToetsStart> {
    const { data } = await apiClient.post<SanneToetsStart>(
      "/api/admin/sanne-toets/nu-draaien",
    );
    return data;
  },
};

export interface CorrectieAantallen {
  totaal: number;
  ongewijzigd: number;
  gewijzigd: number;
  percentage_ongewijzigd: number;
}

export interface CategorieCorrectie {
  categorie: string;
  aantal: number;
}

export const adminCorrectieLogApi = {
  async aantallen(): Promise<CorrectieAantallen> {
    const { data } = await apiClient.get<CorrectieAantallen>(
      "/api/admin/correctie-log/aantallen",
    );
    return data;
  },

  async perCategorie(): Promise<{ items: CategorieCorrectie[] }> {
    const { data } = await apiClient.get<{ items: CategorieCorrectie[] }>(
      "/api/admin/correctie-log/per-categorie",
    );
    return data;
  },

  async stoplicht(): Promise<StoplichtVerdeling> {
    const { data } = await apiClient.get<StoplichtVerdeling>(
      "/api/admin/correctie-log/stoplicht",
    );
    return data;
  },
};

export interface ServicecijfersVolumePunt {
  /** Maandag van de week (YYYY-MM-DD, Europe/Amsterdam). */
  week: string;
  /** "email" | "chat" | "whatsapp" | "telefoon". */
  kanaal: string;
  aantal: number;
}

export interface ServicecijfersMediaanPunt {
  week: string;
  /** Zoals VolumePunt, plus "alle" (kanaal-overstijgende mediaan). */
  kanaal: string;
  mediaan_seconden: number;
  aantal: number;
}

export interface ServicecijfersCategoriePunt {
  week: string;
  categorie: string;
  aantal: number;
}

export interface ServicecijfersCsatPunt {
  week: string;
  gemiddelde: number;
  aantal: number;
}

export interface ServicecijfersAntwoord {
  weken: string[];
  volume: ServicecijfersVolumePunt[];
  eerste_reactie: ServicecijfersMediaanPunt[];
  oplostijd: ServicecijfersMediaanPunt[];
  categorie_volume: ServicecijfersCategoriePunt[];
  /** Mailronde 06-08: gemiddelde mail-CSAT per week (leeg zolang de
   *  meting uit staat of er nog geen inzendingen zijn). */
  mail_csat: ServicecijfersCsatPunt[];
}

export const adminServicecijfersApi = {
  async overzicht(): Promise<ServicecijfersAntwoord> {
    const { data } = await apiClient.get<ServicecijfersAntwoord>(
      "/api/admin/servicecijfers",
    );
    return data;
  },
};

export interface StoplichtGroep {
  sleutel: string;
  groen: number;
  oranje: number;
  rood: number;
}

export interface StoplichtVerdeling {
  per_categorie: StoplichtGroep[];
  per_week: StoplichtGroep[];
}

/** Hulp om API fout-meldingen uit te pakken. */
export function vertaalApiFout(fout: unknown, vangnet = "Er ging iets mis."): string {
  if (!fout) return vangnet;
  if (isToetsWeigering(fout)) {
    return TOETS_WEIGERING_TEKST;
  }
  const ax = fout as AxiosError<{ detail?: string; code?: string }>;
  const detail = ax.response?.data?.detail;
  if (detail && typeof detail === "string") return detail;
  if (ax.message) return ax.message;
  return vangnet;
}

/** Klantvriendelijke variant voor de publieke retourflow (opdracht
 *  21-07): een serverfout (5xx) of netwerkfout toont nooit de rauwe
 *  Axios-tekst ("Request failed with status code 500") maar altijd het
 *  Nederlandse vangnet met klantenservice-verwijzing. Alleen een 4xx
 *  mét detail-tekst (onze eigen nette meldingen) komt door. */
export function vertaalRetourFout(fout: unknown, vangnet: string): string {
  if (!fout) return vangnet;
  const ax = fout as AxiosError<{ detail?: string }>;
  const status = ax.response?.status;
  const detail = ax.response?.data?.detail;
  if (
    typeof status === "number" &&
    status < 500 &&
    typeof detail === "string" &&
    detail.trim()
  ) {
    return detail;
  }
  return vangnet;
}

/** PR #B-4: Niet-admin lookup van medewerkers voor de persona-
 *  toewijzings-dropdown. Sorteert AI-personas eerst, daarna mensen
 *  alfabetisch op voornaam (server-side). */
export const medewerkersApi = {
  async beschikbaarVoorToewijzing(): Promise<MedewerkerVoorToewijzing[]> {
    const { data } = await apiClient.get<MedewerkerVoorToewijzing[]>(
      "/api/medewerkers/beschikbaar-voor-toewijzing",
    );
    return data;
  },

  /** Hotfix 8 mei 2026: render handtekening-HTML voor live preview in
   *  de reply-editor. Niet-admin endpoint zodat alle medewerkers de
   *  preview kunnen zien voor de aan hun toegewezen email. */
  async handtekeningPreview(
    medewerker_id: string,
  ): Promise<MedewerkerPreviewAntwoord> {
    const { data } = await apiClient.get<MedewerkerPreviewAntwoord>(
      `/api/medewerkers/${medewerker_id}/handtekening-preview`,
    );
    return data;
  },
};

// ────── Reviews ──────

export const reviewsApi = {
  async concepten(params: {
    pagina?: number;
    per_pagina?: number;
    status?: string;
    /** "alle", "google" of "webwinkelkeur". */
    bron?: string;
  } = {}): Promise<ReviewConceptLijstAntwoord> {
    const { data } = await apiClient.get<ReviewConceptLijstAntwoord>(
      "/api/reviews/responses/concepten",
      { params },
    );
    return data;
  },

  async detail(id: string): Promise<ReviewResponseAntwoord> {
    const { data } = await apiClient.get<ReviewResponseAntwoord>(
      `/api/reviews/responses/${id}`,
    );
    return data;
  },

  async bewerken(
    id: string,
    body: { response_final: string },
  ): Promise<ReviewResponseAntwoord> {
    const { data } = await apiClient.patch<ReviewResponseAntwoord>(
      `/api/reviews/responses/${id}`,
      body,
    );
    return data;
  },

  async goedkeuren(
    id: string,
    directPlaatsen = false,
  ): Promise<ReviewResponseAntwoord> {
    const { data } = await apiClient.post<ReviewResponseAntwoord>(
      `/api/reviews/responses/${id}/goedkeuren`,
      null,
      { params: { direct_plaatsen: directPlaatsen } },
    );
    return data;
  },

  /**
   * Plaats een goedgekeurde reactie op het platform waar de review
   * vandaan komt. Google en WebwinkelKeur hebben elk hun eigen
   * plaatsroute; de knop hoort dus te weten om welke bron het gaat,
   * anders belandt een Google-reply bij WebwinkelKeur.
   */
  async plaatsen(
    id: string,
    bron: string | null | undefined = "webwinkelkeur",
  ): Promise<ReviewResponseAntwoord> {
    const pad =
      bron === "google"
        ? `/api/google/responses/${id}/plaatsen`
        : `/api/reviews/responses/${id}/plaatsen`;
    // De Google-route geeft een compacter object terug (id, status,
    // posted_at, foutbericht). Dat is precies wat de UI na plaatsing
    // leest; de lijst wordt daarna toch opnieuw opgehaald.
    const { data } = await apiClient.post<ReviewResponseAntwoord>(pad);
    return data;
  },

  async afwijzen(
    id: string,
    body: { afwijzingsreden: string },
  ): Promise<ReviewResponseAntwoord> {
    const { data } = await apiClient.post<ReviewResponseAntwoord>(
      `/api/reviews/responses/${id}/afwijzen`,
      body,
    );
    return data;
  },

  async herstel(id: string): Promise<ReviewResponseAntwoord> {
    const { data } = await apiClient.post<ReviewResponseAntwoord>(
      `/api/reviews/responses/${id}/herstel`,
    );
    return data;
  },

  async kpis(maand?: string): Promise<ReviewKPIs> {
    const { data } = await apiClient.get<ReviewKPIs>(
      "/api/reviews/kpis",
      { params: maand ? { maand } : undefined },
    );
    return data;
  },

  async autopublish(): Promise<ReviewAutopublishAntwoord> {
    const { data } = await apiClient.get<ReviewAutopublishAntwoord>(
      "/api/reviews/autopublish",
    );
    return data;
  },

  async autopublishOpslaan(
    body: ReviewAutopublishWijzigen,
  ): Promise<ReviewAutopublishAntwoord> {
    const { data } = await apiClient.put<ReviewAutopublishAntwoord>(
      "/api/reviews/autopublish",
      body,
    );
    return data;
  },

  async bulkGoedkeuren(
    minScore?: number,
  ): Promise<ReviewBulkGoedkeurenAntwoord> {
    const { data } = await apiClient.post<ReviewBulkGoedkeurenAntwoord>(
      "/api/reviews/responses/bulk-goedkeuren",
      { min_score: minScore ?? null },
    );
    return data;
  },
};

export interface ReviewKPIBron {
  bron: string;
  aantal: number;
  percentage: number;
}

export interface ReviewKPISter {
  score: number;
  aantal: number;
  percentage: number;
}

export interface ReviewKPIPlatform {
  bron: string;
  // null zolang de platformstand nog niet is opgehaald (WKK)
  aantal: number | null;
  gemiddelde: number | null;
}

/** Kant en klaar uitgerekende cijfers voor één bronkaart. */
export interface ReviewKPIBronStatistiek {
  bron: string;
  totaal_aantal: number | null;
  gemiddelde: number | null;
  deze_maand_binnen: number;
  deze_maand_beantwoord: number;
  gemiddelde_maand: number | null;
  sterren_verdeling_maand: ReviewKPISter[];
}

export interface ReviewKPIs {
  maand: string;
  totaal_alle_tijd: number;
  bron_splitsing: ReviewKPIBron[];
  deze_maand_binnen: number;
  deze_maand_beantwoord: number;
  openstaand_nu: number;
  gemiddelde_score_maand: number | null;
  sterren_verdeling_maand: ReviewKPISter[];
  platforms: ReviewKPIPlatform[];
  bron_statistieken: ReviewKPIBronStatistiek[];
  // null zolang de WKK-platformstand nog niet is opgehaald
  totaal_alle_platforms: number | null;
  gemiddelde_alle_platforms: number | null;
  wkk_stand_opgehaald_op: string | null;
  openstaand_werkvoorraad: number;
  laatste_maand_aantal: number;
  wachten_op_beoordeling: number;
  google_beschikbaar: boolean;
}

// ───────── Retourportaal (publiek, blok A) ─────────

export interface RetourArtikelWeergave {
  index: number;
  naam: string;
  aantal: number;
  prijs_cents: number;
  afbeelding_url: string | null;
  op_voorraad: boolean;
}

export interface RetourLookupAntwoord {
  token: string;
  ordernummer: string;
  besteldatum: string;
  artikelen: RetourArtikelWeergave[];
  binnen_termijn: boolean;
  retourkosten_cents: number;
  bestaande_retournummers: string[];
}

export type RetourRedenWaarde =
  | "allergische_reactie"
  | "verkeerd_product_besteld"
  | "verkeerd_product_geleverd"
  | "pakket_beschadigd_bij_aankomst"
  | "verpakking_beschadigd_bij_aankomst"
  | "product_defect_kapot"
  | "product_te_laat_geleverd"
  | "voldoet_niet_aan_verwachtingen"
  | "herroepingsrecht"
  | "andere_reden";

export type RetourOplossingWaarde =
  | "kortingscode"
  | "terugbetaling"
  | "vervanging";

export interface RetourArtikelKeuze {
  artikel_index: number;
  aantal: number;
  reden: RetourRedenWaarde;
  verpakking_geopend?: boolean | null;
  toelichting?: string | null;
  toelichting_keuze?: string | null;
  omschrijving_duidelijk?: boolean | null;
  bijlage_ids?: string[];
}

export interface RetourAanmeldAntwoord {
  retournummer: string;
  status: string;
  kostenoordeel: string;
  retourkosten_cents: number;
  label_beschikbaar: boolean;
  retouradres: string[];
  // Designronde 29-07: zelfde langlevende statustoken als de mail-knop,
  // voor de "Volg je retour"-link op de bevestigingsstap. Null bij
  // geen_retour (niets te volgen).
  status_token: string | null;
  // Blok 2: buiten de termijn, bezorgdatum onzeker of zakelijke klant
  // zonder schade. Geen retour, de pagina zegt "wij kijken ernaar".
  doorgezet?: boolean;
}

export interface RetourBijlageAntwoord {
  bijlage_id: string;
  bestandsnaam: string | null;
  ai_bruikbaar: boolean | null;
  ai_feedback: string | null;
}

export interface RetourInvulhulpArtikel {
  artikel_index: number;
  aantal: number;
  reden: RetourRedenWaarde;
  verpakking_geopend: boolean | null;
  toelichting: string | null;
}

export interface RetourInvulhulpAntwoord {
  artikelen: RetourInvulhulpArtikel[];
  open_vragen: string[];
}

export interface RetourStatusAntwoord {
  retournummer: string;
  status: string;
  kostenoordeel: string;
  retourkosten_cents: number;
  gewenste_oplossing: string | null;
  artikelen: { naam: string; aantal: number; reden: string }[];
  label_beschikbaar: boolean;
  aangemeld_op: string;
  ontvangen_op: string | null;
  afgehandeld_op: string | null;
}

export const retourApi = {
  async lookup(
    ordernummer: string,
    email: string,
  ): Promise<RetourLookupAntwoord> {
    const { data } = await apiClient.post<RetourLookupAntwoord>(
      "/api/retour/lookup",
      { ordernummer, email },
    );
    return data;
  },

  async uploadBijlage(
    token: string,
    bestand: File,
  ): Promise<RetourBijlageAntwoord> {
    const form = new FormData();
    form.append("bestand", bestand);
    const { data } = await apiClient.post<RetourBijlageAntwoord>(
      "/api/retour/bijlagen",
      form,
      { headers: { "X-Retour-Token": token } },
    );
    return data;
  },

  async aanmelden(
    token: string,
    artikelen: RetourArtikelKeuze[],
    oplossing: RetourOplossingWaarde,
    opmerking?: string | null,
  ): Promise<RetourAanmeldAntwoord> {
    const { data } = await apiClient.post<RetourAanmeldAntwoord>(
      "/api/retour/aanmeldingen",
      { artikelen, oplossing, opmerking: opmerking || null },
      { headers: { "X-Retour-Token": token } },
    );
    return data;
  },

  async invulhulp(
    token: string,
    tekst: string,
  ): Promise<RetourInvulhulpAntwoord> {
    const { data } = await apiClient.post<RetourInvulhulpAntwoord>(
      "/api/retour/invulhulp",
      { tekst },
      { headers: { "X-Retour-Token": token } },
    );
    return data;
  },

  async downloadLabel(token: string, retournummer: string): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      `/api/retour/aanmeldingen/${encodeURIComponent(retournummer)}/label`,
      { headers: { "X-Retour-Token": token }, responseType: "blob" },
    );
    return data;
  },

  /** Retourpakbon-PDF (opdracht 21-07): print mee in de doos. */
  async downloadPakbon(token: string, retournummer: string): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      `/api/retour/aanmeldingen/${encodeURIComponent(retournummer)}/pakbon`,
      { headers: { "X-Retour-Token": token }, responseType: "blob" },
    );
    return data;
  },

  async status(
    token: string,
    retournummer: string,
  ): Promise<RetourStatusAntwoord> {
    const { data } = await apiClient.get<RetourStatusAntwoord>(
      `/api/retour/aanmeldingen/${encodeURIComponent(retournummer)}`,
      { headers: { "X-Retour-Token": token } },
    );
    return data;
  },
};

// ───────── Publieke retourstatuspagina (/RetourStatus, 29-07) ─────────

export interface RetourStatusTijdlijnStap {
  stap: "aangemeld" | "onderweg" | "ontvangen" | "afgehandeld";
  staat: "klaar" | "actief" | "wachtend";
  datum: string | null;
  track_trace_url: string | null;
  // Alleen op de onderweg-stap (tracking-status 29-07): vervoerder
  // van het retourlabel voor de "Afgegeven bij PostNL"-tekst.
  vervoerder: string | null;
}

export interface RetourStatusPaginaArtikel {
  naam: string;
  aantal: number;
  prijs_cents: number;
  reden: string;
  verrekend: boolean;
}

export interface RetourStatusPaginaAntwoord {
  retournummer: string;
  ordernummer: string | null;
  voornaam: string | null;
  status: string;
  fase: "loopt" | "beoordeling" | "gesloten";
  aangemeld_op: string;
  tijdlijn: RetourStatusTijdlijnStap[];
  artikelen: RetourStatusPaginaArtikel[];
  artikelbedrag_cents: number;
  verzendkosten_cents: number;
  verrekening_cents: number;
  terug_cents: number;
  gedeeltelijk: boolean;
  afhandeling: "terugbetaling" | "kortingscode" | "vervanging" | null;
  verwerking_werkdagen: number;
  verwerk_uiterlijk: string | null;
  annulering_reden: string | null;
}

export interface RetourStatusKeuze {
  retournummer: string;
  aangemeld_op: string;
  artikelen: string;
}

export interface RetourStatusZoekAntwoord {
  status: RetourStatusPaginaAntwoord | null;
  keuzes: RetourStatusKeuze[];
}

export const retourStatusApi = {
  /** Ingang 1: de statuslink uit de mail (POST — token niet in logs). */
  async viaToken(token: string): Promise<RetourStatusPaginaAntwoord> {
    const { data } = await apiClient.post<RetourStatusPaginaAntwoord>(
      "/api/retour/status-pagina/token",
      { token },
    );
    return data;
  },

  /** Ingang 2: retour- óf ordernummer + e-mailadres (prefix-herkenning
   *  server-side; ORD kan een keuzelijstje opleveren). */
  async zoeken(
    nummer: string,
    email: string,
  ): Promise<RetourStatusZoekAntwoord> {
    const { data } = await apiClient.post<RetourStatusZoekAntwoord>(
      "/api/retour/status-pagina/zoeken",
      { nummer, email },
    );
    return data;
  },
};

// ───────── Publieke volgpagina (/Volgen, sessie 134) ─────────

export interface VolgenZending {
  vervoerder: string | null;
  /** Aantal dozen; null = niet bekend (nooit gokken). */
  aantal_pakketten: number | null;
  stand: "nog_geen_scan" | "onderweg" | "bezorgd" | "afwijking";
  verzonden_op: string | null;
  laatste_scan_op: string | null;
  laatste_scan_plaats: string | null;
  bezorgd_op: string | null;
  /** YYYY-MM-DD, alleen als de vervoerder zelf een dag gaf. */
  verwachte_bezorgdag: string | null;
  /** Volglink zonder postcode; de klant vult die zelf in bij de vervoerder. */
  track_trace_url: string | null;
  /** De volgcode, om over te nemen als er geen link is. */
  track_trace_code: string | null;
}

export type VolgenStap = "besteld" | "ingepakt" | "onderweg" | "bezorgd";

export interface VolgenBestelling {
  ordernummer: string;
  geplaatst_op: string;
  /** Status in gewone taal (kleine letters); null = onbekend. */
  status_tekst: string | null;
  stap: VolgenStap;
  geannuleerd: boolean;
  zendingen: VolgenZending[];
}

export interface VolgenAntwoord {
  soort: "bestelling" | "retour";
  voornaam: string | null;
  bestelling: VolgenBestelling | null;
  retouren: RetourStatusPaginaAntwoord[];
}

export const volgenApi = {
  /** Order- (ORD…) of retournummer (RET-…) plus e-mailadres. 404 bij
   *  elke misser, 429 bij te veel pogingen. */
  async zoeken(nummer: string, email: string): Promise<VolgenAntwoord> {
    const { data } = await apiClient.post<VolgenAntwoord>(
      "/api/retour/volgen",
      { nummer, email },
    );
    return data;
  },
};

// ───────── Admin: Retouren (blok E) ─────────

export interface AdminRetourItem {
  /** Doorloop 06-08: admin-deeplink voor de knop "Open in Lightspeed"
   *  (LIGHTSPEED_ADMIN_URL_TEMPLATE); null bij CSV-geïmporteerde
   *  orders of zonder template. */
  lightspeed_order_url: string | null;
  id: string;
  retournummer: string;
  status: string;
  kostenoordeel: string;
  gewenste_oplossing: string | null;
  ordernummer: string | null;
  klant_id: string;
  /** Blok D (21-07): klantnaam eerste kolom; postcode/huisnummer
   *  dragen alleen het zoekveld (niet getoond). */
  klant_naam: string | null;
  klant_postcode: string | null;
  klant_huisnummer: string | null;
  boven_drempel: boolean;
  ticket_status: string | null;
  kortingscode_status: string | null;
  picqer_verwerking: string | null;
  /** Picqer-retoursync (31-07): leesbaar retournummer ("R2026-4192"),
   *  de magazijnstatus en het moment van inboeken. Zo toont het
   *  overzicht de actuele stand in plaats van te blijven hangen op
   *  "Label aangemaakt". */
  picqer_returnid: string | null;
  picqer_status_naam: string | null;
  picqer_ontvangen_op: string | null;
  label_beschikbaar: boolean;
  aangemeld_op: string;
  /** Eerste scanmoment van de retourzending (tracking-status 29-07). */
  onderweg_op: string | null;
  ontvangen_op: string | null;
  handmatig_aangemaakt: boolean;
}

/** Sessie 169 (C1): wat er na het aanmaken echt gebeurd is. */
export type AdminRetourUitkomst =
  | "label_gemaakt"
  | "label_mislukt"
  | "beoordeling"
  | "niet_aangenomen";

/** Sessie 169 (C1): antwoord van POST /api/admin/retouren/aanmaken. */
export interface AdminRetourAanmaakAntwoord extends AdminRetourItem {
  uitkomst: AdminRetourUitkomst;
  /** Bij niet_aangenomen en beoordeling: waarom, in gewone taal. */
  reden: string | null;
  /** Bij label_mislukt: korte reden, zonder URL's of secrets. */
  label_fout: string | null;
}

export interface AdminRetourOrderzoekAntwoord {
  order_id: string;
  ordernummer: string;
  besteldatum: string;
  artikelen: RetourArtikelWeergave[];
  binnen_termijn: boolean;
  retourkosten_cents: number;
  bestaande_retournummers: string[];
  /** Waarom is `artikelen` leeg (hotfix 14-09)? null = er zijn
   *  gewoon artikelen. Anders: lightspeed_fout, picqer_fout,
   *  geen_artikelen_lightspeed, geen_artikelen_picqer,
   *  geen_artikelen_db of geen_picqer_orderid. */
  artikelen_probleem: string | null;
}

/** De opgeslagen voorbereide retour-actie (besluit JP 15-09). */
export interface AdminRetourVoorstel {
  id: string;
  email_id: string;
  ordernummer: string;
  samenvatting: string;
  kostenoordeel: string;
  retourkosten_cents: number;
  binnen_termijn: boolean;
  termijn_overschrijven: boolean;
  /** Sessie 169 (C2): verpakking geopend bewust opzij gezet. */
  geopend_overschrijven?: boolean;
}

/** Sessie 171 (B1): de body van /aanmaken, ook voor /uitkomst-vooraf. */
export interface AdminRetourAanmaakBody {
  order_id: string;
  artikelen: RetourArtikelKeuze[];
  oplossing: RetourOplossingWaarde;
  opmerking?: string | null;
  termijn_overschrijven?: boolean;
  /** Sessie 169 (C2): alleen "verpakking geopend" gaat opzij. */
  geopend_overschrijven?: boolean;
  klant_niet_mailen?: boolean;
  ook_geen_statusmails?: boolean;
  conversatie_id?: string | null;
}

/** Sessie 171 (B1): antwoord van POST /api/admin/retouren/uitkomst-vooraf.
 *  Dezelfde beslisboom als /aanmaken, maar er wordt niets geschreven. */
export interface AdminRetourUitkomstVooraf {
  uitkomst: "label" | "niet_aangenomen" | "beoordeling";
  reden: string | null;
  uitsluiting: "geopend" | "termijn" | null;
  termijn_verstreken: boolean;
  geopend_overschrijfbaar: boolean;
}

/** Sessie 55: alleen het handmatige aanmaakscherm gebruikt deze API nog.
 *  De afhandelroutes (kortingscode, terugbetaling, vervanging) en de oude
 *  admin-retourschermen zijn weg; afronden gaat via de werkplek. */
export const adminRetourenApi = {
  /** Blok D: order opzoeken voor een handmatige retour (zonder dagcap). */
  async orderzoek(
    ordernummer: string,
    email: string,
  ): Promise<AdminRetourOrderzoekAntwoord> {
    const { data } = await apiClient.post<AdminRetourOrderzoekAntwoord>(
      "/api/admin/retouren/orderzoek",
      { ordernummer, email },
    );
    return data;
  },

  /** Blok D: handmatige retour aanmaken — zelfde weg als de publieke flow.
   *  De drie schakelaars van 14-09 (termijn overschrijven, klant niet
   *  mailen, ook geen statusmails) bestaan alleen op deze route. */
  async aanmaken(
    body: AdminRetourAanmaakBody,
  ): Promise<AdminRetourAanmaakAntwoord> {
    const { data } = await apiClient.post<AdminRetourAanmaakAntwoord>(
      "/api/admin/retouren/aanmaken",
      body,
    );
    return data;
  },

  /** Sessie 171: wat er zou gebeuren als je nu opslaat. Schrijft niets. */
  async uitkomstVooraf(
    body: AdminRetourAanmaakBody,
  ): Promise<AdminRetourUitkomstVooraf> {
    const { data } = await apiClient.post<AdminRetourUitkomstVooraf>(
      "/api/admin/retouren/uitkomst-vooraf",
      body,
    );
    return data;
  },

  /** Sessie 169 (C4): nieuwe labelpoging na label_mislukt. */
  async labelOpnieuw(retourId: string): Promise<AdminRetourItem> {
    const { data } = await apiClient.post<AdminRetourItem>(
      `/api/admin/retouren/${encodeURIComponent(retourId)}/label/opnieuw`,
    );
    return data;
  },

  /** Sessie 169 (C4): label en pakbon opnieuw naar de klant mailen. */
  async mailLabelOpnieuw(retourId: string): Promise<AdminRetourItem> {
    const { data } = await apiClient.post<AdminRetourItem>(
      `/api/admin/retouren/${encodeURIComponent(retourId)}/mail/label`,
    );
    return data;
  },

  /** Sessie 169 (C4): label-PDF als blob (Bearer via apiClient). */
  async labelPdf(retourId: string): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      `/api/admin/retouren/${encodeURIComponent(retourId)}/label`,
      { responseType: "blob" },
    );
    return data;
  },

  /** Sessie 169 (C4): pakbon-PDF als blob (Bearer via apiClient). */
  async pakbonPdf(retourId: string): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      `/api/admin/retouren/${encodeURIComponent(retourId)}/pakbon`,
      { responseType: "blob" },
    );
    return data;
  },

  /** Hetzelfde aanmaakscherm, maar als voorbereide actie (besluit JP
   *  15-09): er wordt niets aangemaakt en er gebeurt niets bij TAP —
   *  het label ontstaat pas als JP het antwoord verstuurt. */
  async voorstelOpslaan(body: {
    email_id: string;
    order_id: string;
    artikelen: RetourArtikelKeuze[];
    oplossing: RetourOplossingWaarde;
    opmerking?: string | null;
    termijn_overschrijven?: boolean;
    /** Sessie 169 (C2): alleen "verpakking geopend" gaat opzij. */
    geopend_overschrijven?: boolean;
    klant_niet_mailen?: boolean;
    ook_geen_statusmails?: boolean;
  }): Promise<AdminRetourVoorstel> {
    const { data } = await apiClient.post<AdminRetourVoorstel>(
      "/api/admin/retouren/voorstel",
      body,
    );
    return data;
  },
};

// ── Gesprekken (telefonie, 05-08) ───────────────────────────────

export type GesprekTab =
  | "alle"
  | "terugbellen"
  | "inkomend"
  | "uitgaand"
  | "gemist";

export type GesprekCategorie =
  | "beantwoord"
  | "gemist"
  | "buiten_openingstijden"
  | "voicemail"
  | "geen_gehoor"
  /** Overdrachtsronde 15-09: een terugbelverzoek uit chat of WhatsApp. */
  | "terugbelverzoek";

export interface GesprekRij {
  id: string;
  richting: "inkomend" | "uitgaand";
  gemist: boolean;
  telefoonnummer: string;
  klant_id: string | null;
  klant_naam: string | null;
  gestart_op: string;
  duur_seconden: number | null;
  uitkomst: string | null;
  categorie: GesprekCategorie;
  ai_samenvatting: string | null;
  /** "rinkel" | "sanne" | null — null: nog geen samenvatting. */
  samenvatting_bron: string | null;
  ai_categorie: string | null;
  terugbel_nodig: boolean;
  /** Fixronde 06-08: alleen bij een echte opname (of transcript) mag
   *  de lijst "Transcript wordt gemaakt" tonen. */
  heeft_opname: boolean;
  heeft_transcript: boolean;
  /** Overdrachtsronde 15-09: op de terugbellijst staan ook
   *  terugbelverzoeken uit chat en WhatsApp. "rinkel" = echt
   *  telefoongesprek. */
  bron: "rinkel" | "chat" | "whatsapp";
  /** Titel van een terugbelverzoek uit chat/WhatsApp; null bij telefonie. */
  onderwerp: string | null;
  /** Doorklik naar het bronbericht van een terugbelverzoek. */
  chat_sessie_id: string | null;
}

export interface GesprekkenLijstAntwoord {
  gesprekken: GesprekRij[];
  totaal: number;
  pagina: number;
  per_pagina: number;
  terugbel_teller: number;
}

export interface GesprekTellersAntwoord {
  terugbellen: number;
}

/** Periodetegels boven de gesprekkenlijst (mock-telefonie-v2, 07-08). */
export interface GesprekkenKopAntwoord {
  terug_te_bellen: number;
  vandaag_totaal: number;
  vandaag_beantwoord: number;
  vandaag_gemiddelde_duur_seconden: number | null;
  week_totaal: number;
  week_gemiddelde_duur_seconden: number | null;
  totaal: number;
}

export interface GesprekKlantContext {
  laatste_bestelling_nummer: string | null;
  laatste_bestelling_datum: string | null;
  laatste_bestelling_status: string | null;
  open_mails: number;
  open_retouren: number;
}

export interface GesprekDetail extends Omit<GesprekRij, "terugbel_nodig"> {
  beeindigd_op: string | null;
  transcript: string | null;
  opname_status: "beschikbaar" | "verlopen" | "geen";
  opname_url: string | null;
  markering_bewaren: boolean;
  bewaren_tot: string;
  terugbel_nodig: boolean;
  terugbel_afgehandeld_op: string | null;
  terugbel_afhandeling: string | null;
  klant_context: GesprekKlantContext | null;
  /** Detail-subregel (mock): "klant sinds 2022, 9 orders". */
  klant_sinds: string | null;
  klant_aantal_orders: number | null;
}

export const gesprekkenApi = {
  async lijst(
    tab: GesprekTab = "alle",
    pagina = 1,
    perPagina = 50,
  ): Promise<GesprekkenLijstAntwoord> {
    const { data } = await apiClient.get<GesprekkenLijstAntwoord>(
      "/api/gesprekken",
      { params: { tab, pagina, per_pagina: perPagina } },
    );
    return data;
  },

  /** Badge-teller voor de zijbalk (gepolld). */
  async tellers(): Promise<GesprekTellersAntwoord> {
    const { data } = await apiClient.get<GesprekTellersAntwoord>(
      "/api/gesprekken/tellers",
    );
    return data;
  },

  /** Cijfers voor de periodetegels: nu / vandaag / deze week / totaal. */
  async kop(): Promise<GesprekkenKopAntwoord> {
    const { data } = await apiClient.get<GesprekkenKopAntwoord>(
      "/api/gesprekken/kop",
    );
    return data;
  },

  async detail(id: string): Promise<GesprekDetail> {
    const { data } = await apiClient.get<GesprekDetail>(
      `/api/gesprekken/${id}`,
    );
    return data;
  },

  /** Markeer de opname als langer te bewaren (AVG-uitzondering). */
  async langerBewaren(id: string): Promise<GesprekDetail> {
    const { data } = await apiClient.post<GesprekDetail>(
      `/api/gesprekken/${id}/bewaren`,
    );
    return data;
  },

  async terugbelAfvinken(id: string): Promise<GesprekDetail> {
    const { data } = await apiClient.post<GesprekDetail>(
      `/api/gesprekken/${id}/terugbel-afgehandeld`,
    );
    return data;
  },

  /**
   * Click-to-call: Rinkel belt eerst het eigen toestel, dan de klant.
   * `klantId` (waar bekend) voedt de bel-intent zodat het gesprek aan
   * de juiste klant hangt.
   */
  async bellen(telefoonnummer: string, klantId?: string | null): Promise<void> {
    await apiClient.post("/api/gesprekken/bellen", {
      telefoonnummer,
      klant_id: klantId ?? null,
    });
  },

  /** Verwijder een gesprek (soft delete, met audit in de backend). */
  async verwijderen(id: string): Promise<void> {
    await apiClient.delete(`/api/gesprekken/${id}`);
  },
};

/** Kanaaloverstijgende werkvoorraad (inbox-herindeling 18-08). */
export const werkvoorraadApi = {
  async lijst(params: {
    weergave: WerkvoorraadWeergave;
    tab: WerkvoorraadTab;
  }): Promise<WerkvoorraadAntwoord> {
    const { data } = await apiClient.get<WerkvoorraadAntwoord>(
      "/api/werkvoorraad",
      { params },
    );
    return data;
  },

  /** Sidebar-tellers: per definitie gelijk aan wat de lijst toont. */
  async tellers(): Promise<WerkvoorraadTellersAntwoord> {
    const { data } = await apiClient.get<WerkvoorraadTellersAntwoord>(
      "/api/werkvoorraad/tellers",
    );
    return data;
  },
};

// ── Zoeken 1.0: Beheer > Zoeken (03-09) ────────────────────────────

export const zoekAdminApi = {
  async overzicht(dagen: number): Promise<ZoekOverzichtAntwoord> {
    const { data } = await apiClient.get<ZoekOverzichtAntwoord>(
      "/api/admin/zoeken/overzicht",
      { params: { dagen } },
    );
    return data;
  },
  async opdrachten(dagen: number): Promise<ZoekOpdrachtenAntwoord> {
    const { data } = await apiClient.get<ZoekOpdrachtenAntwoord>(
      "/api/admin/zoeken/opdrachten",
      { params: { dagen } },
    );
    return data;
  },
  async zonderResultaat(dagen: number): Promise<ZoekNulAntwoord> {
    const { data } = await apiClient.get<ZoekNulAntwoord>(
      "/api/admin/zoeken/zonder-resultaat",
      { params: { dagen } },
    );
    return data;
  },
  async synoniemen(): Promise<ZoekSynoniemenAntwoord> {
    const { data } = await apiClient.get<ZoekSynoniemenAntwoord>(
      "/api/admin/zoeken/synoniemen",
    );
    return data;
  },
  async maakSynoniem(invoer: ZoekSynoniemInvoer): Promise<ZoekSynoniemenAntwoord> {
    const { data } = await apiClient.post<ZoekSynoniemenAntwoord>(
      "/api/admin/zoeken/synoniemen",
      invoer,
    );
    return data;
  },
  async wijzigSynoniem(
    id: string,
    invoer: ZoekSynoniemInvoer,
  ): Promise<ZoekSynoniemenAntwoord> {
    const { data } = await apiClient.put<ZoekSynoniemenAntwoord>(
      `/api/admin/zoeken/synoniemen/${id}`,
      invoer,
    );
    return data;
  },
  async verwijderSynoniem(id: string): Promise<ZoekSynoniemenAntwoord> {
    const { data } = await apiClient.delete<ZoekSynoniemenAntwoord>(
      `/api/admin/zoeken/synoniemen/${id}`,
    );
    return data;
  },
  async instellingen(): Promise<ZoekInstellingenAntwoord> {
    const { data } = await apiClient.get<ZoekInstellingenAntwoord>(
      "/api/admin/zoeken/instellingen",
    );
    return data;
  },
  async wijzigInstellingen(
    invoer: ZoekInstellingenWijzigen,
  ): Promise<ZoekInstellingenAntwoord> {
    const { data } = await apiClient.put<ZoekInstellingenAntwoord>(
      "/api/admin/zoeken/instellingen",
      invoer,
    );
    return data;
  },
  async herindexeer(): Promise<ZoekHerindexAntwoord> {
    const { data } = await apiClient.post<ZoekHerindexAntwoord>(
      "/api/admin/zoeken/herindexeer",
    );
    return data;
  },
};

/** Historie over alle kanalen (ronde H2): afgehandeld werk, zoekbaar. */
export const historieApi = {
  async lijst(params: HistorieParams): Promise<HistorieAntwoord> {
    const { data } = await apiClient.get<HistorieAntwoord>("/api/historie", {
      params,
    });
    return data;
  },
};

/** Sessie 53: stand van een gemiddelde tegen het doel, met het woord erbij. */
export type MailconceptStand = "goed" | "let_op" | null;

export interface MailconceptDag {
  datum: string;
  aantal: number;
  gemiddeld_eur: number | null;
  totaal_eur: number;
  stand: MailconceptStand;
  chip: string | null;
  /** Motor sessie 8H: het deel van V8 in `aantal` en `totaal_eur`. */
  v8_aantal?: number;
  v8_totaal_eur?: number;
}

/** Motor sessie 8H: het deel van een motor in de mailconcept-kosten. */
export interface MailconceptOpbouw {
  /** `oud` (de oudste motor) of `v8`. */
  motor: string;
  label: string;
  aantal: number;
  gemiddeld_eur: number | null;
  totaal_eur: number;
}

export interface MailconceptVraagtype {
  vraagtype: string | null;
  aantal: number;
  gemiddeld_eur: number | null;
  stand: MailconceptStand;
  chip: string | null;
}

export interface MailconceptKosten {
  doel_eur: number;
  begin: string;
  eind: string;
  aantal: number;
  gemiddeld_eur: number | null;
  totaal_eur: number;
  stand: MailconceptStand;
  chip: string | null;
  dagen: MailconceptDag[];
  per_vraagtype: MailconceptVraagtype[];
  /** Motor sessie 8H: oude motor en V8 apart (samen gelijk aan het totaal). */
  opbouw?: MailconceptOpbouw[];
}

/** Sessie 53: kosten per mailconcept, veertien dagen (Gezondheid). */
export const mailconceptKostenApi = {
  async haal(): Promise<MailconceptKosten> {
    const { data } = await apiClient.get<MailconceptKosten>(
      "/api/werkplek/gezondheid/mailkosten",
    );
    return data;
  },
};

/** Motor sessie 8H: een periode van de regel V8 in Gezondheid. */
export interface V8Periode {
  /** `vandaag`, `week` (laatste 7 dagen) of `maand` (laatste 30 dagen). */
  sleutel: string;
  label: string;
  begin: string;
  eind: string;
  beurten: number;
  mislukt: number;
  handen: number;
  kosten_eur: number;
}

export interface V8Gezondheid {
  bijgewerkt_op: string;
  perioden: V8Periode[];
}

/** Motor sessie 8H: beurten, handen en kosten van V8 (Gezondheid). */
export const v8GezondheidApi = {
  async haal(): Promise<V8Gezondheid> {
    const { data } = await apiClient.get<V8Gezondheid>(
      "/api/werkplek/gezondheid/v8",
    );
    return data;
  },
};

/** Sessie 53 blok 3: stand van het antwoord per vraagtype (stoplicht). */
export type VraagtypeStand = "rood" | "oranje" | "akkoord" | "groen";

/** Correcties per soort uit correctie_log; `verzending` = gewijzigd verstuurd. */
export interface CorrectiesPerSoort {
  verzending: number;
  copiloot: number;
  afwijzing: number;
  afgerond_zonder_antwoord: number;
}

export interface VraagtypeGezondheid {
  /** `null` = zonder vraagtype. */
  vraagtype: string | null;
  naam: string;
  /** `null` = geen stoplichtrij voor dit vraagtype. */
  stand: VraagtypeStand | null;
  teller: number | null;
  n: number | null;
  verkeer: number;
  ongewijzigd: number;
  correcties: number;
  per_soort: CorrectiesPerSoort;
  concepten: number;
  gemiddeld_eur: number | null;
  /** Hoogste stand die de motor mag voorstellen (sessie 53 blok 3). */
  max_stand?: VraagtypeStand | null;
  /** Hoeveel van de correcties via de copiloot liepen. */
  via_copiloot?: number;
  /** Door Sanne zelf verstuurd: niemand las het, dus geen oordeel. */
  door_sanne?: number;
  /** Sessie 87: de meter "naar groen" (drie eisen); het scherm komt in 87b. */
  meter?: VraagtypeMeter | null;
}

export interface VraagtypenGezondheid {
  begin: string;
  eind: string;
  dagen: number;
  /** Meeste verkeer bovenaan. */
  vraagtypen: VraagtypeGezondheid[];
}

/** Sessie 53 blok 3: Gezondheid per vraagtype, dertig dagen. */
export const vraagtypenGezondheidApi = {
  async haal(): Promise<VraagtypenGezondheid> {
    const { data } = await apiClient.get<VraagtypenGezondheid>(
      "/api/werkplek/gezondheid/vraagtypen",
    );
    return data;
  },
};

export type { AxiosRequestConfig };
